import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { StaffCalendar,type CalendarGuestHold } from './StaffCalendar';
import {ProfileAvatar} from './ProfileAvatar';
import {StaffWaitlistPanel} from './StaffWaitlistPanel';
import {NewCustomerDialog} from './NewCustomerDialog';
import {SearchPersonRow} from './SearchPersonRow';

type SpaLocation = { code:string; name:string; timezone:string };
type Appointment = {
  appointmentId:string;customerId:string;customerName:string;status:string;
  serviceCode:string;serviceName:string;resourceId:string;resourceName:string;
  startsAt:string;serviceEndsAt:string;occupancyEndsAt:string;
};
type RoomBlock = {
  blockId:string;resourceId:string;resourceName:string;reason:string;category:string;
  startsAt:string;endsAt:string;
};
type BlockableResource = {resourceId:string;code:string;name:string};
type CalendarConfiguredStart = {resourceId:string;startMinute:number;source?:string};
type Customer = {customerId:string;displayName:string;homeLocationCode:string|null;
  actorId?:string|null;actorKind?:'TEAM_MEMBER'|'CUSTOMER'|null;
  email?:string|null;phone?:string|null};
type ProfileConsent='UNKNOWN'|'OPTED_IN'|'OPTED_OUT';
type EmailPreferences={
  appointmentConfirmation:boolean;appointmentReminder:boolean;
  saleReceipt:boolean;nonEssential:boolean;mailingList:boolean;
};
const preferenceFields=[
  ['appointmentConfirmation','Appointment confirmation'],
  ['appointmentReminder','Appointment reminder'],
  ['saleReceipt','Sale receipt'],
  ['nonEssential','Non-essential emails'],
  ['mailingList','Mailing list'],
] as const;
type ConsentEvidence='IN_PERSON'|'WRITTEN_REQUEST'|'ONLINE_REQUEST'|'EXISTING_DOCUMENTATION';
type CustomerRegistrationFields={
  address:string|null;postalCode:string|null;
  emergencyContactName:string|null;emergencyContactPhone:string|null;
  heardFrom:string|null;
};
const emptyCustomerRegistration=():CustomerRegistrationFields=>({
  address:null,postalCode:null,emergencyContactName:null,
  emergencyContactPhone:null,heardFrom:null,
});
type CustomerProfile = Customer & {
  actorId?:string|null;lastLoginAt?:string|null;
  version:number;email:string|null;phoneE164:string|null;
  emailConsent:ProfileConsent;smsConsent:ProfileConsent;
  emailConsentSource:string|null;smsConsentSource:string|null;
  emailPreferences:EmailPreferences|null;
  birthdayMonth:number|null;birthdayDay:number|null;birthdayYear:number|null;
  registrationDetails?:CustomerRegistrationFields;
};
type HistoryEvent={type:'confirmed'|'rescheduled'|'cancelled'|'checked_in'|'completed'|'no_show';at:string};
type HistoryEntry={
  appointmentId:string;locationCode:string;locationName:string;locationTimezone:string;
  status:string;serviceName:string;resourceName:string;startsAt:string;
  serviceEndsAt:string;occupancyEndsAt:string;events:HistoryEvent[];
};
type HistoryPage={
  appointments:HistoryEntry[];hasMore:boolean;
  nextCursor:{startsAt:string;appointmentId:string}|null;
};
type AppointmentHistoryFilter=
  'All'|'Active'|'Active: Checked Out'|'Active: Returned'|'No-Shows'|
  'No-Shows: NOT Cancelled'|'No-Shows: Cancelled'|'Cancelled'|
  'Active or Cancelled'|'Moved'|'Blocked';
const appointmentHistoryFilters:AppointmentHistoryFilter[]=[
  'All','Active','Active: Checked Out','Active: Returned','No-Shows',
  'No-Shows: NOT Cancelled','No-Shows: Cancelled','Cancelled',
  'Active or Cancelled','Moved','Blocked',
];
export function matchesHistoryFilter(item:HistoryEntry,filter:AppointmentHistoryFilter):boolean{
  const status=item.status.toUpperCase().replace(/\s+/g,'_');
  const events=item.events??[];
  const noShow=status==='NO_SHOW'||events.some(e=>e.type==='no_show');
  const cancelled=status==='CANCELLED'||events.some(e=>e.type==='cancelled');
  const moved=status==='MOVED';
  const blocked=status==='BLOCKED';
  switch(filter){
    case 'All':return true;
    case 'Active':return !cancelled&&!moved&&!blocked;
    case 'Active: Checked Out':return !cancelled&&status==='CHECKED_OUT';
    case 'Active: Returned':return !cancelled&&status==='RETURNED';
    case 'No-Shows':return noShow;
    case 'No-Shows: NOT Cancelled':return noShow&&!cancelled;
    case 'No-Shows: Cancelled':return noShow&&cancelled;
    case 'Cancelled':return cancelled;
    case 'Active or Cancelled':return !moved&&!blocked;
    case 'Moved':return moved;
    case 'Blocked':return blocked;
  }
}
type ProfileCredit={
  creditId:string;serviceCode:string;serviceName:string;
  remainingUses:number;issuedAt:string;expiresAt:string|null;
  availability:'available'|'unavailable';
};
type ProfileCreditResult={credits:ProfileCredit[];availability:'available'|'unavailable'};
function validCredits(data:unknown,kind:'available'|'unavailable'):data is ProfileCreditResult {
  if(!data||typeof data!=='object')return false;
  const page=data as Partial<ProfileCreditResult>;
  return page.availability===kind&&Array.isArray(page.credits)&&
    page.credits.every(x=>x&&typeof x.creditId==='string'&&
      typeof x.serviceCode==='string'&&typeof x.serviceName==='string'&&
      x.serviceName.trim().length>0&&Number.isSafeInteger(x.remainingUses)&&
      x.remainingUses>=0&&typeof x.issuedAt==='string'&&
      Number.isFinite(Date.parse(x.issuedAt))&&
      (x.expiresAt===null||(typeof x.expiresAt==='string'&&
        Number.isFinite(Date.parse(x.expiresAt))))&&x.availability===kind);
}
function groupedAvailableCredits(credits:ProfileCredit[]){
  const groups=new Map<string,{code:string;name:string;quantity:number}>();
  for(const item of credits){
    if(item.remainingUses===0)continue;
    const previous=groups.get(item.serviceCode);
    if(previous)previous.quantity+=item.remainingUses;
    else groups.set(item.serviceCode,{code:item.serviceCode,
      name:item.serviceName,quantity:item.remainingUses});
  }
  return [...groups.values()];
}
/** Protect the profile UI from crashing if a partial/malformed history response
 * is returned. Do not silently treat missing financial or appointment data as
 * a valid history record. */
function validHistoryPage(data:unknown):data is HistoryPage {
  if(!data||typeof data!=='object')return false;
  const record=data as Partial<HistoryPage>;
  return Array.isArray(record.appointments)&&typeof record.hasMore==='boolean'&&
    (record.nextCursor===null||(!!record.nextCursor&&
      typeof record.nextCursor.startsAt==='string'&&
      typeof record.nextCursor.appointmentId==='string'))&&
    record.appointments.every(item=>item&&typeof item.appointmentId==='string'&&
      typeof item.status==='string'&&typeof item.serviceName==='string'&&
      typeof item.resourceName==='string'&&typeof item.locationName==='string'&&
      typeof item.locationTimezone==='string'&&typeof item.startsAt==='string'&&
      typeof item.serviceEndsAt==='string'&&typeof item.occupancyEndsAt==='string'&&
      Array.isArray(item.events)&&item.events.every(e=>e&&typeof e.type==='string'));
}
type BookingOption = {serviceId:string;code:string;name:string;durationMinutes:number;
  cleanupMinutes:number;priceCents?:number|null;rooms:{resourceId:string;code:string;name:string}[]};
type PendingHold = {holdId:string;customerId:string};

type Service = {code:string;name:string;durationMinutes:number;cleanupMinutes:number};
type Slot = {startsAt:string;serviceEndsAt:string;occupancyEndsAt:string;availableResourceCount:number};
type OwnAppointment = {appointmentId:string;locationName:string;locationTimezone:string;serviceName:string;startsAt:string;endsAt:string;status:string};
type AsyncData<T> = {state:'loading'|'ready'|'signin'|'denied'|'error';data:T;message?:string};

function localDay(instant:Date|string, timeZone:string) {
  const date=typeof instant==='string'?new Date(instant):instant;
  const parts=new Intl.DateTimeFormat('en-US',{
    timeZone,year:'numeric',month:'2-digit',day:'2-digit',
  }).formatToParts(date);
  const year=parts.find(item=>item.type==='year')?.value;
  const month=parts.find(item=>item.type==='month')?.value;
  const day=parts.find(item=>item.type==='day')?.value;
  return year+'-'+month+'-'+day;
}
function utcWindow(day:string) {
  const baseline=Date.parse(day+'T00:00:00.000Z');
  return {
    from:new Date(baseline-86400000).toISOString(),
    to:new Date(baseline+2*86400000).toISOString(),
  };
}
function readable(instant:string,timeZone:string) {
  return new Intl.DateTimeFormat('en-US',{
    timeZone,hour:'numeric',minute:'2-digit',
  }).format(new Date(instant));
}
async function requestData<T>(url:string,signal:AbortSignal):Promise<AsyncData<T>> {
  const response=await fetch(url,{credentials:'same-origin',cache:'no-store',signal});
  if(response.status===401)return {state:'signin',data:null as T};
  if(response.status===403)return {state:'denied',data:null as T};
  if(!response.ok)throw new Error('This information is unavailable.');
  const body=await response.json() as {ok:boolean;data:T};
  if(!body.ok||body.data===null||typeof body.data!=='object')
    throw new Error('This information is unavailable.');
  return {state:'ready',data:body.data};
}
function Message({state,message,loginMessage}:{state:AsyncData<unknown>['state'];message?:string;loginMessage?:string}) {
  if(state==='loading')return <p role="status">Loading…</p>;
  if(state==='signin')return <p role="status">{loginMessage??"Sign in to view this location's Team Member schedule."}</p>;
  if(state==='denied')return <p role="alert">Your account does not have access to this information.</p>;
  if(state==='error')return <p role="alert">{message??'Scheduling information is temporarily unavailable.'}</p>;
  return null;
}

/** Map local office wall-clock time to exactly one UTC instant.
 * Reject nonexistent DST times and ambiguous repeated clock times rather
 * than silently selecting the wrong Float Room appointment hour.
 */
export function localBookingTimeToUTC(local:string,timeZone:string):string {
  if(!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local))
    throw new RangeError('Enter a valid date and time.');
  const base=Date.parse(local+':00.000Z');
  if(!Number.isFinite(base))throw new RangeError('Enter a valid date and time.');
  const possible:string[]=[];
  const formatter=new Intl.DateTimeFormat('en-US',{
    timeZone,year:'numeric',month:'2-digit',day:'2-digit',
    hour:'2-digit',minute:'2-digit',hourCycle:'h23',
  });
  for(let offset=-14*60;offset<=14*60;offset+=15){
    const candidate=new Date(base-offset*60000);
    const parts=formatter.formatToParts(candidate);
    const part=(type:string)=>parts.find(p=>p.type===type)?.value??'';
    if(part('year')+'-'+part('month')+'-'+part('day')+'T'+
      part('hour')+':'+part('minute')===local){
      possible.push(candidate.toISOString());
    }
  }
  if(possible.length!==1)
    throw new RangeError('This local time is ambiguous or does not exist. Choose a different time.');
  return possible[0];
}

export function StaffWorkspace({location,headerSearch,profileSelection,onGoPublic,onOpenCancellations,onOpenRoom,onOpenPerson,onOpenCustomerProfile,onOpenSale,onBeginCustomerSale}:{
  location:SpaLocation;
  headerSearch?:{term:string;version:number};
  profileSelection?:{customer:Customer;version:number;locationCode:string};
  onGoPublic:()=>void;
  onOpenCancellations?:(day:string)=>void;
  onOpenRoom?:(resourceId:string)=>void;
  onOpenPerson?:(actorId:string,displayName:string)=>void;
  onOpenCustomerProfile?:(customer:Customer)=>void;
  onOpenSale:(sale:{appointmentId:string;customerId:string;
    customerName:string;serviceName:string;serviceCode:string;
    saleId:string;payerOther:boolean;
    preselectedDiscount?:{id:string;code:string;title:string}|null;
    linkedAppointments?:Array<{
      appointmentId:string;customerId:string;customerName:string;
      serviceName:string;serviceCode:string;saleId:string}>})=>void;
  onBeginCustomerSale:(customer:{customerId:string;displayName:string})=>void;
}) {
  const [day,setDay]=useState(()=>localDay(new Date(),location.timezone));
  const [waitlistOpen,setWaitlistOpen]=useState(false);
  const [calendar,setCalendar]=useState<AsyncData<{appointments:Appointment[];checkInEnabled?:boolean}>>({
    state:'loading',data:{appointments:[]},
  });
  const [blocks,setBlocks]=useState<AsyncData<{blocks:RoomBlock[]}>>({
    state:'loading',data:{blocks:[]},
  });
  const [guestHolds,setGuestHolds]=useState<AsyncData<{holds:CalendarGuestHold[]}>>({
    state:'loading',data:{holds:[]},
  });
  const [,setCustomers]=useState<AsyncData<{customers:Customer[];hasMore:boolean}>|null>(null);
  const searchController=useRef<AbortController|null>(null);
  const [profileTarget,setProfileTarget]=useState<Customer|null>(null);
  const [profileFocused,setProfileFocused]=useState(false);
  const [profileTab,setProfileTab]=useState<'Appointments'|'Service Credits'|'Notes'|'Purchases'>('Appointments');
  const [appointmentHistoryFilter,setAppointmentHistoryFilter]=useState<AppointmentHistoryFilter>('All');
  const [profileNotes,setProfileNotes]=useState<AsyncData<{notes:Array<{
    noteId:string;text:string;important:boolean;version:number;
    createdAt:string;authorName:string;
  }>}>|null>(null);
  const [profileNoteText,setProfileNoteText]=useState('');
  const [profileNoteImportant,setProfileNoteImportant]=useState(false);
  const [profileNoteBusy,setProfileNoteBusy]=useState(false);
  const [profileNoteStatus,setProfileNoteStatus]=useState('');
  const [profileNoteRefresh,setProfileNoteRefresh]=useState(0);
  type PurchasePage={
    purchases:Array<{id:string;occurredAt:string;kind:'CASH'|'STRIPE_TEST';
      description:string;locationName:string;subtotalCents:number}>;
    hasMore:boolean;nextCursor:{occurredAt:string;purchaseId:string}|null;
  };
  const [purchaseHistory,setPurchaseHistory]=useState<AsyncData<PurchasePage>|null>(null);
  const [purchaseBusy,setPurchaseBusy]=useState(false);
  const [purchaseError,setPurchaseError]=useState('');
  const purchaseRequest=useRef<AbortController|null>(null);
  const [waiverOpen,setWaiverOpen]=useState(false);
  type WaiverPage={waivers:Array<{acceptanceId:string;title:string;revision:number;
    signedAt:string;inheritedFromMergedAccount:boolean}>;hasMore:boolean;
    nextCursor:{signedAt:string;acceptanceId:string}|null};
  const [waivers,setWaivers]=useState<AsyncData<WaiverPage>|null>(null);
  const [waiverBusy,setWaiverBusy]=useState(false);
  const [waiverError,setWaiverError]=useState('');
  const waiverRequest=useRef<AbortController|null>(null);
  const [creditAvailability,setCreditAvailability]=useState<'available'|'unavailable'>('available');
  const [editCreditExpiry,setEditCreditExpiry]=useState<string|null>(null);
  const [expiresNever,setExpiresNever]=useState(true);
  const [expiresOn,setExpiresOn]=useState('');
  const [expiryBusy,setExpiryBusy]=useState(false);
  const [expiryStatus,setExpiryStatus]=useState('');
  const [profileCredits,setProfileCredits]=useState<AsyncData<ProfileCreditResult>|null>(null);
  const creditRequest=useRef<AbortController|null>(null);
  const [availableCredits,setAvailableCredits]=useState<AsyncData<ProfileCreditResult>|null>(null);
  const availableCreditRequest=useRef<AbortController|null>(null);
  const [profileData,setProfileData]=useState<AsyncData<{profile:CustomerProfile}>|null>(null);
  const [profileHistory,setProfileHistory]=useState<AsyncData<HistoryPage>|null>(null);
  const [profileHistoryStatus,setProfileHistoryStatus]=useState('');
  const [profileHistoryBusy,setProfileHistoryBusy]=useState(false);
  const historyRequest=useRef<AbortController|null>(null);
  const [profileName,setProfileName]=useState('');
  const [profileEmail,setProfileEmail]=useState('');
  const [profilePhone,setProfilePhone]=useState('');
  const [profileBirthdayMonth,setProfileBirthdayMonth]=useState('');
  const [profileBirthdayDay,setProfileBirthdayDay]=useState('');
  const [profileBirthdayYear,setProfileBirthdayYear]=useState('');
  const [profileRegistration,setProfileRegistration]=useState<CustomerRegistrationFields>(
    emptyCustomerRegistration);
  const [profileReferralOptions,setProfileReferralOptions]=useState<string[]>([]);
  const [profileEmailConsent,setProfileEmailConsent]=useState<ProfileConsent>('UNKNOWN');
  const [profileSmsConsent,setProfileSmsConsent]=useState<ProfileConsent>('UNKNOWN');
  const [profileEmailPreferences,setProfileEmailPreferences]=useState<EmailPreferences|null>(null);
  const [profileEmailEvidence,setProfileEmailEvidence]=useState<ConsentEvidence|''>('');
  const [profileSmsEvidence,setProfileSmsEvidence]=useState<ConsentEvidence|''>('');
  const [profileBusy,setProfileBusy]=useState(false);
  const [profileMessage,setProfileMessage]=useState('');
  const [profileDuplicateMatches,setProfileDuplicateMatches]=useState<Customer[]|null>(null);
  const profileKey=useRef<string|null>(null);
  const profileRequest=useRef<AbortController|null>(null);
  const [refresh,setRefresh]=useState(0);
  useEffect(()=>{
    const refreshAfterSale=()=>setRefresh(value=>value+1);
    window.addEventListener('ecfs:refresh-sales-calendar',refreshAfterSale);
    return ()=>window.removeEventListener('ecfs:refresh-sales-calendar',refreshAfterSale);
  },[]);
  const [options,setOptions]=useState<AsyncData<{services:BookingOption[];writesEnabled:boolean}>>({
    state:'loading',data:{services:[],writesEnabled:false},
  });
  const [selectedGuest,setSelectedGuest]=useState<Customer|null>(null);
  const [serviceId,setServiceId]=useState('');
  const [resourceId,setResourceId]=useState('');
  const [localStart,setLocalStart]=useState('');
  const [pendingHold,setPendingHold]=useState<PendingHold|null>(null);
  const [bookingStatus,setBookingStatus]=useState('');
  const [bookingBusy,setBookingBusy]=useState(false);
  const [slotPopup,setSlotPopup]=useState<{resourceId:string;startMinute:number;
    left:number;top:number}|null>(null);
  const [slotQuery,setSlotQuery]=useState('');
  const [slotMatches,setSlotMatches]=useState<Customer[]>([]);
  const [slotAllowed,setSlotAllowed]=useState<string[]|null>(null);
  const [slotEligibilityMessage,setSlotEligibilityMessage]=useState('');
  const [slotEligibilityState,setSlotEligibilityState]=useState<'idle'|'checking'|'ready'|'error'>('idle');
  const [slotTeamMatches,setSlotTeamMatches]=useState<Array<{
    staffId:string;displayName:string;email?:string|null;phone?:string|null;
    customerId?:string|null;
  }>>([]);
  const [slotStaffToAdd,setSlotStaffToAdd]=useState<{
    staffId:string;displayName:string;email?:string|null;phone?:string|null;
  }|null>(null);
  const [autoBookCreated,setAutoBookCreated]=useState(false);
  const [slotSearchStatus,setSlotSearchStatus]=useState('');
  const [slotSearchBusy,setSlotSearchBusy]=useState(false);
  const [creatingFromSlot,setCreatingFromSlot]=useState(false);
  const [attendanceBusy,setAttendanceBusy]=useState('');
  const [attendanceMessage,setAttendanceMessage]=useState('');
  const [checkoutBusy,setCheckoutBusy]=useState(false);
  const [rescheduleTarget,setRescheduleTarget]=useState<Appointment|null>(null);

  const bookingInProgress=useRef(false);
  const [blockable,setBlockable]=useState<AsyncData<{
    resources:BlockableResource[];writesEnabled:boolean}>>({
    state:'loading',data:{resources:[],writesEnabled:false},
  });
  const [blockRange,setBlockRange]=useState<{
    resourceIds:string[];startMinute:number;endMinute:number}|null>(null);
  const [blockReason,setBlockReason]=useState('');
  const [blockBusy,setBlockBusy]=useState(false);
  const [calendarResources,setCalendarResources]=useState<AsyncData<{resources:BlockableResource[]}>>({
    state:'loading',data:{resources:[]},
  });
  const [configuredStarts,setConfiguredStarts]=useState<AsyncData<{starts:CalendarConfiguredStart[]}>>({
    state:'loading',data:{starts:[]},
  });


  useEffect(()=>{
    setDay(localDay(new Date(),location.timezone));
    searchController.current?.abort();
    setCustomers(null);
    profileRequest.current?.abort();
    historyRequest.current?.abort();creditRequest.current?.abort();
    availableCreditRequest.current?.abort();
    setProfileCredits(null);setAvailableCredits(null);setCreditAvailability('available');
    setEditCreditExpiry(null);setExpiryStatus('');
    setProfileHistory(null);
    setProfileHistoryStatus('');
    setProfileHistoryBusy(false);
    setProfileTarget(null);
    setProfileData(null);
    setProfileMessage('');
    setProfileDuplicateMatches(null);
    profileKey.current=null;
    setSelectedGuest(null);
    setLocalStart('');
    setPendingHold(null);
    setBookingStatus('');
    setSlotPopup(null);setSlotQuery('');setSlotMatches([]);setSlotTeamMatches([]);
    setSlotAllowed(null);setSlotEligibilityMessage('');setSlotEligibilityState('idle');
    setSlotStaffToAdd(null);setAutoBookCreated(false);
    setAttendanceBusy('');
    setAttendanceMessage('');
  },[location.code,location.timezone]);

  useEffect(()=>{
    const controller=new AbortController();
    const {from,to}=utcWindow(day);
    const query=new URLSearchParams({location:location.code,from,to});
    setCalendar({state:'loading',data:{appointments:[]}});
    setBlocks({state:'loading',data:{blocks:[]}});
    setGuestHolds({state:'loading',data:{holds:[]}});
    // Owner-requested in-progress holds become visible without pressing Refresh.
    // Only this small feed polls; do not reload all schedules every 15 seconds.
    let checking=false;
    const readHolds=async()=>{
      if(checking||controller.signal.aborted)return;
      checking=true;
      try{
        const next=await requestData<{holds:CalendarGuestHold[]}>(
          '/api/v1/staff/guest-holds?'+query,controller.signal);
        if(!controller.signal.aborted)setGuestHolds(next);
      }catch{
        if(!controller.signal.aborted)setGuestHolds({state:'error',data:{holds:[]}});
      }finally{checking=false;}
    };
    void readHolds();
    const guestHoldPoll=window.setInterval(()=>void readHolds(),15000);
    void requestData<{appointments:Appointment[];checkInEnabled?:boolean}>('/api/v1/staff/appointments?'+query,controller.signal)
      .then(next=>{if(!controller.signal.aborted)setCalendar(next);})
      .catch(()=>{if(!controller.signal.aborted)setCalendar({
        state:'error',data:{appointments:[]},
      });});
    void requestData<{blocks:RoomBlock[]}>('/api/v1/staff/blocks?'+query,controller.signal)
      .then(next=>{if(!controller.signal.aborted)setBlocks(next);})
      .catch(()=>{if(!controller.signal.aborted)setBlocks({
        state:'error',data:{blocks:[]},
      });});
    return ()=>{window.clearInterval(guestHoldPoll);controller.abort();};
  },[location.code,day,refresh]);

  useEffect(()=>{
    const controller=new AbortController();
    setCalendarResources({state:'loading',data:{resources:[]}});
    setBlockable({state:'loading',data:{resources:[],writesEnabled:false}});
    void requestData<{resources:BlockableResource[];writesEnabled:boolean}>(
      '/api/v1/staff/blockable-resources?'+new URLSearchParams({location:location.code}),
      controller.signal)
      .then(next=>{if(!controller.signal.aborted)setBlockable(next);})
      .catch(()=>{if(!controller.signal.aborted)setBlockable({
        state:'error',data:{resources:[],writesEnabled:false},
      });});
    void requestData<{resources:BlockableResource[]}>(
      '/api/v1/staff/calendar-resources?'+new URLSearchParams({location:location.code}),
      controller.signal,
    ).then(next=>{
      if(!controller.signal.aborted)setCalendarResources(next);
    }).catch(()=>{
      if(!controller.signal.aborted)setCalendarResources({
        state:'error',data:{resources:[]},
      });
    });
    return ()=>controller.abort();
  },[location.code]);

  useEffect(()=>{
    const controller=new AbortController();
    setConfiguredStarts({state:'loading',data:{starts:[]}});
    void requestData<{starts:CalendarConfiguredStart[]}>(
      '/api/v1/staff/calendar-starts?'+new URLSearchParams({
        location:location.code,date:day,
      }),controller.signal,
    ).then(next=>{
      if(!controller.signal.aborted)setConfiguredStarts(next);
    }).catch(()=>{
      if(!controller.signal.aborted)setConfiguredStarts({
        state:'error',data:{starts:[]},
      });
    });
    return ()=>controller.abort();
  },[location.code,day,refresh]);

  useEffect(()=>{
    const controller=new AbortController();
    setOptions({state:'loading',data:{services:[],writesEnabled:false}});
    void requestData<{services:BookingOption[];writesEnabled:boolean}>(
      '/api/v1/staff/booking-options?'+new URLSearchParams({location:location.code}),
      controller.signal,
    ).then(next=>{
      if(controller.signal.aborted)return;
      setOptions(next);
      const first=next.data?.services[0];
      setServiceId(first?.serviceId??'');
      setResourceId(first?.rooms[0]?.resourceId??'');
    }).catch(()=>{
      if(!controller.signal.aborted)setOptions({
        state:'error',data:{services:[],writesEnabled:false},
      });
    });
    return ()=>controller.abort();
  },[location.code,refresh]);

  const appointments=useMemo(()=>calendar.data?.appointments
    ?.filter(item=>localDay(item.startsAt,location.timezone)===day)??[],
    [calendar.data,day,location.timezone]);
  const openBlockRange=(resourceIds:string[],startMinute:number,endMinute:number)=>{
    if(!blockable.data.writesEnabled)return;
    setSlotPopup(null);setBlockReason('');
    setBlockRange({resourceIds,startMinute,endMinute});
  };
  const timeFromMinute=(minute:number)=>String(Math.floor(minute/60)).padStart(2,'0')+
    ':'+String(minute%60).padStart(2,'0');
  const minuteFromTime=(value:string)=>{
    const match=/^([01][0-9]|2[0-3]):([0-5][0-9])$/.exec(value);
    return match?Number(match[1])*60+Number(match[2]):-1;
  };
  const saveBlockRange=async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();
    if(!blockRange||blockBusy)return;
    const {startMinute,endMinute,resourceIds}=blockRange;
    if(endMinute<=startMinute||endMinute>1440||startMinute<0||
      blockReason.trim().length<3||!resourceIds.length){
      setAttendanceMessage('Enter a reason and a valid end time after the start time.');
      return;
    }
    setBlockBusy(true);
    const created:string[]=[];
    try{
      const startsAt=localBookingTimeToUTC(day+'T'+timeFromMinute(startMinute),
        location.timezone);
      const endsAt=localBookingTimeToUTC(
        endMinute===1440?new Date(Date.parse(day+'T12:00:00Z')+86400000)
          .toISOString().slice(0,10)+'T00:00':day+'T'+timeFromMinute(endMinute),
        location.timezone);
      for(const resourceId of resourceIds){
        const response=await fetch('/api/v1/staff/blocks',{
          method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:location.code,resourceId,startsAt,endsAt,
            reason:blockReason.trim(),category:'UNAVAILABLE',
            idempotencyKey:crypto.randomUUID()}),
        });
        const json=await response.json() as {ok:boolean;data?:{blockId:string}};
        if(!response.ok||!json.ok||!json.data?.blockId)
          throw Error('A selected room or time cannot be blocked.');
        created.push(json.data.blockId);
      }
      setBlockRange(null);setBlockReason('');
      setAttendanceMessage(resourceIds.length+' room block'+
        (resourceIds.length===1?'':'s')+' saved.');
      setRefresh(value=>value+1);
    }catch(error){
      let releaseFailures=0;
      for(const blockId of created){
        try{
          const response=await fetch('/api/v1/staff/blocks/release',{
            method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},
            body:JSON.stringify({location:location.code,blockId}),
          });
          if(!response.ok)releaseFailures++;
        }catch{releaseFailures++;}
      }
      setAttendanceMessage((error instanceof Error?error.message:'Unable to save blockout.')+
        (releaseFailures?' '+releaseFailures+' block(s) need staff review.':' No partial blocks remain.'));
      setRefresh(value=>value+1);
    }finally{setBlockBusy(false);}
  };
  const releaseBlock=async(blockId:string)=>{
    try{
      const response=await fetch('/api/v1/staff/blocks/release',{
        method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:location.code,blockId}),
      });
      const result=await response.json() as {ok:boolean};
      if(!response.ok||!result.ok)throw Error('BLOCK_RELEASE_FAILED');
      setAttendanceMessage('Room block removed.');setRefresh(v=>v+1);
    }catch{setAttendanceMessage('Unable to remove room block.');}
  };
  const releaseBlocks=async(blockIds:string[])=>{
    const failed:string[]=[];
    for(const blockId of [...new Set(blockIds)]){
      try{
        const response=await fetch('/api/v1/staff/blocks/release',{
          method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:location.code,blockId}),
        });
        const json=await response.json() as {ok:boolean};
        if(!response.ok||!json.ok)failed.push(blockId);
      }catch{failed.push(blockId);}
    }
    setAttendanceMessage(failed.length?
      failed.length+' room block(s) could not be removed. Refresh and retry.':
      blockIds.length+' room block(s) removed.');
    setRefresh(value=>value+1);
  };
  const releaseGuestHold=async(holdId:string)=>{
    try{
      const response=await fetch('/api/v1/staff/guest-holds/release',{
        method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:location.code,holdId}),
      });
      const result=await response.json() as {ok:boolean};
      if(!response.ok||!result.ok)throw Error('HOLD_RELEASE_FAILED');
      setAttendanceMessage('Temporary booking hold released.');
      setRefresh(v=>v+1);
    }catch{setAttendanceMessage('Unable to release this temporary booking hold.');}
  };
  const maintenance=useMemo(()=>blocks.data?.blocks
    ?.filter(item=>localDay(item.startsAt,location.timezone)<=day &&
      localDay(item.endsAt,location.timezone)>=day)??[],
    [blocks.data,day,location.timezone]);

  useEffect(()=>{
    if(!headerSearch?.term||headerSearch.term.trim().length<2)return;
    const term=headerSearch.term.trim();
    setProfileFocused(false);
    searchController.current?.abort();
    const controller=new AbortController();
    searchController.current=controller;
    setCustomers({state:'loading',data:{customers:[],hasMore:false}});
    void requestData<{customers:Customer[];hasMore:boolean}>(
      '/api/v1/staff/customers?'+new URLSearchParams({location:location.code,q:term}),
      controller.signal,
    ).then(result=>{
      if(controller.signal.aborted)return;
      setCustomers(result);
      document.getElementById('ecfs-staff-customer-search')?.scrollIntoView({block:'start'});
    }).catch(()=>{
      if(!controller.signal.aborted)setCustomers({state:'error',data:{customers:[],hasMore:false}});
    });
    return ()=>controller.abort();
  },[headerSearch?.version,location.code]);

  // Separate permission from profile editing. History is always fetched
  // for the selected exact customer ID, not inferred from a name or email.
  const loadProfileHistory=async(
    guest:Customer,cursor?:{startsAt:string;appointmentId:string},
  )=>{
    historyRequest.current?.abort();
    const controller=new AbortController();
    historyRequest.current=controller;
    if(!cursor)setProfileHistory({state:'loading',data:null as never});
    else setProfileHistoryBusy(true);
    setProfileHistoryStatus('');
    const params=new URLSearchParams({
      location:location.code,limit:'20',
      ...(cursor?{beforeStart:cursor.startsAt,beforeId:cursor.appointmentId}:{}),
    });
    try{
      const result=await requestData<HistoryPage>(
        '/api/v1/staff/customers/'+encodeURIComponent(guest.customerId)+
          '/appointments?'+params,controller.signal);
      if(controller.signal.aborted)return;
      if(result.state==='ready'&&!validHistoryPage(result.data))
        throw new Error('Malformed appointment history response');
      setProfileHistory(previous=>{
        if(!cursor||!previous||previous.state!=='ready'||result.state!=='ready')
          return result;
        return {state:'ready',data:{
          ...result.data,
          appointments:[...previous.data.appointments,...result.data.appointments],
        }};
      });
    }catch{
      if(!controller.signal.aborted){
        if(cursor)setProfileHistoryStatus('Could not load older appointments. Try again.');
        else setProfileHistory({state:'error',data:null as never});
      }
    }finally{
      if(!controller.signal.aborted)setProfileHistoryBusy(false);
    }
  };

  const loadWaivers=async(guest:Customer,
    cursor?:{signedAt:string;acceptanceId:string})=>{
    waiverRequest.current?.abort();
    const controller=new AbortController();
    waiverRequest.current=controller;
    if(cursor)setWaiverBusy(true);
    else setWaivers({state:'loading',data:null as never});
    setWaiverError('');
    try{
      const params=new URLSearchParams({location:location.code,limit:'30',
        ...(cursor?{beforeSignedAt:cursor.signedAt,beforeId:cursor.acceptanceId}:{})});
      const result=await requestData<WaiverPage>(
        '/api/v1/staff/customers/'+encodeURIComponent(guest.customerId)+
        '/waivers?'+params,controller.signal);
      if(controller.signal.aborted)return;
      setWaivers(previous=>cursor&&previous?.state==='ready'&&result.state==='ready'
        ?{state:'ready',data:{...result.data,
          waivers:[...previous.data.waivers,...result.data.waivers]}}:result);
    }catch{
      if(!controller.signal.aborted){
        if(cursor)setWaiverError('Could not load older waivers.');
        else setWaivers({state:'error',data:null as never});
      }
    }finally{if(!controller.signal.aborted)setWaiverBusy(false);}
  };
  const loadPurchases=async(guest:Customer,
    cursor?:{occurredAt:string;purchaseId:string})=>{
    purchaseRequest.current?.abort();
    const controller=new AbortController();
    purchaseRequest.current=controller;
    if(cursor)setPurchaseBusy(true);
    else setPurchaseHistory({state:'loading',data:null as never});
    setPurchaseError('');
    const params=new URLSearchParams({location:location.code,limit:'30',
      ...(cursor?{beforeAt:cursor.occurredAt,beforeId:cursor.purchaseId}:{})});
    try{
      const result=await requestData<PurchasePage>(
        '/api/v1/staff/customers/'+encodeURIComponent(guest.customerId)+
          '/purchases?'+params,controller.signal);
      if(controller.signal.aborted)return;
      setPurchaseHistory(previous=>
        cursor&&previous?.state==='ready'&&result.state==='ready'
        ?{state:'ready',data:{...result.data,
          purchases:[...previous.data.purchases,...result.data.purchases]}}:result);
    }catch{
      if(!controller.signal.aborted){
        if(cursor)setPurchaseError('Could not load older purchases.');
        else setPurchaseHistory({state:'error',data:null as never});
      }
    }finally{if(!controller.signal.aborted)setPurchaseBusy(false);}
  };
  const loadProfile=async(guest:Customer)=>{
    purchaseRequest.current?.abort();setPurchaseHistory(null);setPurchaseError('');
    waiverRequest.current?.abort();setWaiverOpen(false);setWaivers(null);
    creditRequest.current?.abort();availableCreditRequest.current?.abort();
    setProfileCredits(null);setAvailableCredits(null);
    profileRequest.current?.abort();
    const controller=new AbortController();
    profileRequest.current=controller;
    setProfileTarget(guest);
    void loadProfileHistory(guest);
    setProfileData({state:'loading',data:null as never});
    setProfileMessage('');
    setProfileDuplicateMatches(null);
    profileKey.current=null;
    try{
      const response=await requestData<{profile:CustomerProfile}>(
        '/api/v1/staff/customers/'+encodeURIComponent(guest.customerId)+'?'+
          new URLSearchParams({location:location.code}),
        controller.signal);
      if(controller.signal.aborted)return;
      setProfileData(response);
      if(response.state==='ready'){
        const profile=response.data.profile;
        // Staff book services using one underlying person. The linked
        // customer ledger is NOT another person or a second profile page.
        if(profile.actorKind==='TEAM_MEMBER'&&profile.actorId&&onOpenPerson){
          onOpenPerson(profile.actorId,profile.displayName);
          return;
        }
        // A calendar preview only carries the appointment's customer ID/name.
        // The authoritative profile supplies the home location and latest name.
        setProfileTarget(current=>current?.customerId===guest.customerId?{
          customerId:guest.customerId,
          displayName:profile.displayName,
          homeLocationCode:profile.homeLocationCode,
        }:current);
        setProfileName(profile.displayName);
        setProfileEmail(profile.email??'');
        setProfilePhone(profile.phoneE164??'');
        setProfileBirthdayMonth(profile.birthdayMonth?.toString()??'');
        setProfileBirthdayDay(profile.birthdayDay?.toString()??'');
        setProfileBirthdayYear(profile.birthdayYear?.toString()??'');
        setProfileRegistration({...emptyCustomerRegistration(),
          ...profile.registrationDetails});
        setProfileEmailConsent(profile.emailConsent);
        setProfileSmsConsent(profile.smsConsent);
        setProfileEmailPreferences(profile.emailPreferences);
        setProfileEmailEvidence('');
        setProfileSmsEvidence('');
      }
    }catch{
      if(!controller.signal.aborted)setProfileData({state:'error',data:null as never});
    }
  };
  useEffect(()=>{
    if(!profileSelection||profileSelection.locationCode!==location.code)return;
    setProfileFocused(true);
    setProfileTab('Appointments');
    setAppointmentHistoryFilter('All');
    void loadProfile(profileSelection.customer);
  },[profileSelection?.version,location.code]);

  useEffect(()=>{
    if(!profileFocused||profileTab!=='Purchases'||!profileTarget||
      profileData?.state!=='ready')return;
    void loadPurchases(profileTarget);
    return ()=>purchaseRequest.current?.abort();
  },[profileFocused,profileTab,profileTarget?.customerId,profileData?.state,location.code]);
  useEffect(()=>{
    if(!profileFocused||profileTab!=='Notes'||!profileTarget||
      profileData?.state!=='ready')return;
    const controller=new AbortController();
    setProfileNotes({state:'loading',data:null as never});
    setProfileNoteStatus('');
    void requestData<{notes:Array<{
      noteId:string;text:string;important:boolean;version:number;
      createdAt:string;authorName:string;
    }>}>('/api/v1/staff/notes/customer/'+encodeURIComponent(profileTarget.customerId)+
      '?'+new URLSearchParams({location:location.code}),controller.signal)
      .then(result=>{
        if(!controller.signal.aborted)setProfileNotes(result);
      }).catch(()=>{
        if(!controller.signal.aborted)setProfileNotes({state:'error',data:null as never});
      });
    return ()=>controller.abort();
  },[profileFocused,profileTab,profileTarget?.customerId,profileData?.state,
    location.code,profileNoteRefresh]);
  const addCustomerNote=async()=>{
    if(!profileTarget||!profileNoteText.trim()||profileNoteBusy)return;
    setProfileNoteBusy(true);setProfileNoteStatus('');
    try{
      const res=await fetch('/api/v1/staff/notes/customer/'+
        encodeURIComponent(profileTarget.customerId),{
        method:'POST',credentials:'same-origin',headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,text:profileNoteText.trim(),
          important:profileNoteImportant,idempotencyKey:crypto.randomUUID()}),
      });
      const body=await res.json() as {ok:boolean;error?:{code:string}};
      if(!res.ok||!body.ok)throw Error(body.error?.code??'NOTE_SAVE_FAILED');
      setProfileNoteText('');setProfileNoteImportant(false);
      setProfileNoteRefresh(n=>n+1);
    }catch(error){setProfileNoteStatus(error instanceof Error?error.message:'Note could not be posted.');}
    finally{setProfileNoteBusy(false);}
  };
  const modifyCustomerNote=async(noteId:string,expectedVersion:number,important?:boolean)=>{
    if(!profileTarget||profileNoteBusy)return;
    setProfileNoteBusy(true);setProfileNoteStatus('');
    try{
      const res=await fetch('/api/v1/staff/notes/customer/'+
        encodeURIComponent(profileTarget.customerId)+'/'+encodeURIComponent(noteId),{
        method:'PATCH',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,expectedVersion,
          ...(important===undefined?{delete:true}:{important})}),
      });
      const body=await res.json() as {ok:boolean;error?:{code:string}};
      if(!res.ok||!body.ok)throw Error(body.error?.code??'NOTE_UPDATE_FAILED');
      setProfileNoteRefresh(n=>n+1);
    }catch(error){setProfileNoteStatus(error instanceof Error?error.message:'Note could not be updated.');}
    finally{setProfileNoteBusy(false);}
  };

  // The available summary above tabs and the Available detail tab use ONE
  // current, permissioned issued-credit read even on Appointments.
  useEffect(()=>{
    if(!profileFocused||!profileTarget||profileData?.state!=='ready'||
      profileData.data.profile.customerId!==profileTarget.customerId)return;
    const controller=new AbortController();
    availableCreditRequest.current?.abort();availableCreditRequest.current=controller;
    setAvailableCredits({state:'loading',data:null as never});
    const query=new URLSearchParams({location:location.code,availability:'available'});
    void requestData<ProfileCreditResult>(
      '/api/v1/staff/customers/'+encodeURIComponent(profileTarget.customerId)+
      '/credits?'+query,controller.signal,
    ).then(result=>{
      if(controller.signal.aborted)return;
      if(result.state==='ready'&&!validCredits(result.data,'available'))
        throw Error('Invalid credit ledger response');
      setAvailableCredits(result);
    }).catch(()=>{
      if(!controller.signal.aborted)setAvailableCredits({state:'error',data:null as never});
    });
    return ()=>controller.abort();
  },[profileFocused,profileTarget?.customerId,profileData?.state,location.code,refresh]);
  // Unavailable ledger rows are a separate status query. Switching back to
  // Available must show exactly what the top-of-profile summary uses.
  useEffect(()=>{
    if(!profileFocused||profileTab!=='Service Credits'||
      creditAvailability!=='unavailable'||!profileTarget||
      profileData?.state!=='ready'||
      profileData.data.profile.customerId!==profileTarget.customerId)return;
    const controller=new AbortController();
    creditRequest.current?.abort();creditRequest.current=controller;
    setProfileCredits({state:'loading',data:null as never});
    const query=new URLSearchParams({location:location.code,availability:'unavailable'});
    void requestData<ProfileCreditResult>(
      '/api/v1/staff/customers/'+encodeURIComponent(profileTarget.customerId)+
      '/credits?'+query,controller.signal,
    ).then(result=>{
      if(controller.signal.aborted)return;
      if(result.state==='ready'&&!validCredits(result.data,'unavailable'))
        throw Error('Invalid credit ledger response');
      setProfileCredits(result);
    }).catch(()=>{
      if(!controller.signal.aborted)setProfileCredits({state:'error',data:null as never});
    });
    return ()=>controller.abort();
  },[profileFocused,profileTab,profileTarget?.customerId,creditAvailability,
    profileData?.state,location.code,refresh]);
  const visibleCredits=creditAvailability==='available'?availableCredits:profileCredits;
  const creditSummary=availableCredits?.state==='ready'
    ?groupedAvailableCredits(availableCredits.data.credits):null;

  const toCreditExpiryDay=(iso:string)=>{
    const date=new Date(Date.parse(iso)-1);
    const parts=new Intl.DateTimeFormat('en-US',{timeZone:location.timezone,
      year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(date);
    const part=(type:string)=>parts.find(item=>item.type===type)?.value??'';
    return part('year')+'-'+part('month')+'-'+part('day');
  };
  const saveCreditExpiration=async(creditId:string,expectedExpiresAt:string|null)=>{
    if(!profileTarget||expiryBusy||(!expiresNever&&!expiresOn))return;
    setExpiryBusy(true);setExpiryStatus('');
    try{
      const response=await fetch('/api/v1/staff/customers/'+
        encodeURIComponent(profileTarget.customerId)+'/credits/'+
        encodeURIComponent(creditId)+'/expiration',{
          method:'PATCH',credentials:'same-origin',
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:location.code,
            expiresOn:expiresNever?null:expiresOn,expectedExpiresAt}),
        });
      const body=await response.json() as {ok:boolean;error?:{code:string}};
      if(!response.ok||!body.ok)throw Error(response.status===409
        ?'Credit expiration changed elsewhere. Reload this profile to review it.'
        :response.status===403?'Credit expiration edit permission denied.':
          body.error?.code??'Credit expiration could not be saved.');
      setEditCreditExpiry(null);
      setExpiryStatus('Credit expiration saved.');
      setRefresh(current=>current+1);
    }catch(error){
      setExpiryStatus(error instanceof Error?error.message:
        'Credit expiration could not be saved.');
    }finally{setExpiryBusy(false);}
  };
  useEffect(()=>{
    if(!profileFocused||!profileTarget)return;
    const controller=new AbortController();
    setProfileReferralOptions([]);
    void fetch('/api/v1/public/signup-options?'+new URLSearchParams({
      location:location.code,
    }),{credentials:'same-origin',cache:'no-store',signal:controller.signal})
      .then(async res=>{
        if(!res.ok)throw Error('Signup options unavailable');
        const json=await res.json() as {ok:boolean;data?:{referralOptions?:string[]}};
        if(!controller.signal.aborted&&json.ok&&Array.isArray(json.data?.referralOptions))
          setProfileReferralOptions(json.data.referralOptions);
      }).catch(()=>{/* Existing recorded value remains visible without fake choices. */});
    return ()=>controller.abort();
  },[profileFocused,profileTarget?.customerId,location.code]);
  const editRegistration=(key:keyof CustomerRegistrationFields,value:string)=>
    {setProfileRegistration(p=>({...p,[key]:value||null}));profileKey.current=null;};
  const saveProfile=async(event?:FormEvent<HTMLFormElement>,reviewedIds?:string[])=>{
    event?.preventDefault();
    if(profileBusy||profileData?.state!=='ready'||!profileTarget)return;
    const previous=profileData.data.profile;
    const patch:Record<string,unknown>={};
    if(profileName.trim()!==previous.displayName)patch.displayName=profileName.trim();
    if(profileEmail.trim()!==(previous.email??''))patch.email=profileEmail.trim()||null;
    if(profilePhone.trim()!==(previous.phoneE164??''))patch.phoneE164=profilePhone.trim()||null;
    const bdMonth=profileBirthdayMonth?Number(profileBirthdayMonth):null;
    const bdDay=profileBirthdayDay?Number(profileBirthdayDay):null;
    const bdYear=profileBirthdayYear?Number(profileBirthdayYear):null;
    if(bdMonth!==(previous.birthdayMonth??null)||bdDay!==(previous.birthdayDay??null)||
      bdYear!==(previous.birthdayYear??null)){
      if((bdMonth===null)!==(bdDay===null)||(bdYear!==null&&bdMonth===null))return;
      patch.birthday={month:bdMonth,day:bdDay,year:bdYear};
    }
    if(JSON.stringify(profileRegistration)!==
      JSON.stringify({...emptyCustomerRegistration(),...previous.registrationDetails}))
      patch.registrationDetails=profileRegistration;
    if(profileEmailConsent!==previous.emailConsent){
      if(profileEmailConsent!=='UNKNOWN'&&!profileEmailEvidence){
        setProfileMessage('Choose where the email preference was recorded.');
        return;
      }
      patch.emailConsent={status:profileEmailConsent,
        ...(profileEmailConsent==='UNKNOWN'?{}:{source:profileEmailEvidence})};
    }
    if(profileEmailPreferences&&
      JSON.stringify(profileEmailPreferences)!==JSON.stringify(previous.emailPreferences))
      patch.emailPreferences=profileEmailPreferences;
    if(profileSmsConsent!==previous.smsConsent){
      if(profileSmsConsent!=='UNKNOWN'&&!profileSmsEvidence){
        setProfileMessage('Choose where the SMS preference was recorded.');
        return;
      }
      patch.smsConsent={status:profileSmsConsent,
        ...(profileSmsConsent==='UNKNOWN'?{}:{source:profileSmsEvidence})};
    }
    if(!Object.keys(patch).length){
      setProfileMessage('No changes to save.');
      return;
    }
    if(!profileKey.current)profileKey.current=crypto.randomUUID()+'-customer-profile';
    setProfileBusy(true);setProfileMessage('');
    try{
      const response=await fetch('/api/v1/staff/customers/'+encodeURIComponent(profileTarget.customerId),{
        method:'PATCH',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:location.code,expectedVersion:previous.version,
          idempotencyKey:profileKey.current,patch,
          ...(reviewedIds?{reviewedExistingCustomerIds:reviewedIds}:{}),
        }),
      });
      const body=await response.json() as {
        ok:boolean;data?:{status:string;version?:number;matches?:Customer[]};
        error?:{code?:string;message?:string};
      };
      if(response.status===409&&body.data?.status==='review_required'){
        setProfileDuplicateMatches(body.data.matches??[]);
        profileKey.current=null;
        setProfileMessage('Another profile has this name. Review it before confirming a distinct person.');
        return;
      }
      if(response.status===409&&body.error?.code==='CUSTOMER_CHANGED'){
        profileKey.current=null;
        setProfileDuplicateMatches(null);
        setProfileMessage('This profile changed elsewhere. Reload it before editing again.');
        return;
      }
      if(!response.ok||!body.ok||body.data?.status!=='updated')
        throw new Error(body.error?.message??'Unable to save profile.');
      setProfileDuplicateMatches(null);
      profileKey.current=null;
      const updatedName=profileName.trim();
      const revised:Customer={...profileTarget,displayName:updatedName};
      setSelectedGuest(current=>current?.customerId===revised.customerId?revised:current);
      setCustomers(current=>current?.state==='ready'?{
        ...current,
        data:{...current.data,customers:current.data.customers.map(item=>
          item.customerId===revised.customerId?revised:item)},
      }:current);
      await loadProfile(revised);
      setProfileMessage('Profile saved.');
      setRefresh(v=>v+1);
    }catch(error){
      setProfileMessage(error instanceof Error?error.message:'Unable to save customer profile.');
    }finally{setProfileBusy(false);}
  };

  // Owner's full-page profile edits do not require a separate Save click.
  // Wait for consent evidence when it was changed and then persist through
  // the same authorized, optimistic, audited customer PATCH transaction.
  useEffect(()=>{
    if(!profileFocused||profileBusy||
      profileData?.state!=='ready'||!profileTarget)return;
    const p=profileData.data.profile;
    const nameChanged=profileName.trim()!==p.displayName;
    const emailChanged=profileEmail.trim()!==(p.email??'');
    const phoneChanged=profilePhone.trim()!==(p.phoneE164??'');
    const ec=profileEmailConsent!==p.emailConsent;
    const sc=profileSmsConsent!==p.smsConsent;
    const bd=profileBirthdayMonth!==String(p.birthdayMonth??'')||
      profileBirthdayDay!==String(p.birthdayDay??'')||
      profileBirthdayYear!==String(p.birthdayYear??'');
    const ep=profileEmailPreferences!==null&&
      JSON.stringify(profileEmailPreferences)!==JSON.stringify(p.emailPreferences);
    const rd=JSON.stringify(profileRegistration)!==
      JSON.stringify({...emptyCustomerRegistration(),...p.registrationDetails});
    if(!nameChanged&&!emailChanged&&!phoneChanged&&!bd&&!ec&&!sc&&!ep&&!rd)return;
    if(bd&&((!profileBirthdayMonth)!==(!profileBirthdayDay)||
      (!!profileBirthdayYear&&!profileBirthdayMonth)))return;
    if((ec&&profileEmailConsent!=='UNKNOWN'&&!profileEmailEvidence)||
      (sc&&profileSmsConsent!=='UNKNOWN'&&!profileSmsEvidence))return;
    const timer=window.setTimeout(()=>void saveProfile(),650);
    return ()=>window.clearTimeout(timer);
  },[profileFocused,profileTab,profileBusy,profileData?.state,
    profileData?.data?.profile.version,profileTarget?.customerId,
    profileName,profileEmail,profilePhone,
    profileBirthdayMonth,profileBirthdayDay,profileBirthdayYear,
    profileRegistration,profileEmailConsent,profileSmsConsent,
    profileEmailPreferences,profileEmailEvidence,profileSmsEvidence]);

  const checkoutAppointment=async(item:Appointment,forceNewSale=false,payerOther=false,
    pendingDiscount?:{id:string;code:string;title:string}|null)=>{
    if(checkoutBusy||options.state!=='ready'||!options.data.writesEnabled)return;
    setCheckoutBusy(true);setAttendanceMessage('');
    try{
      const response=await fetch('/api/v1/staff/bookings/checkout',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:location.code,appointmentId:item.appointmentId,
          forceNewSale:forceNewSale||payerOther||Boolean(pendingDiscount),
          ...(pendingDiscount?{preselectedDiscountId:pendingDiscount.id}:{}),
        }),
      });
      const body=await response.json() as {ok:boolean;data?:{
        status:'checked_out'|'sale_required';saleId?:string;
        linkedAppointments?:Array<{appointmentId:string;customerId:string;
          customerName:string;serviceName:string;serviceCode:string;saleId:string}>;
      };error?:{code?:string;message?:string}};
      if(!response.ok||!body.ok||!body.data)
        throw Error(body.error?.message??body.error?.code??'Unable to check out.');
      setRefresh(n=>n+1);
      if(body.data.status==='checked_out'){
        setAttendanceMessage('Checked out with existing service credit.');
      }else if(body.data.saleId){
        onOpenSale({appointmentId:item.appointmentId,
          customerId:item.customerId,customerName:item.customerName,
          serviceName:item.serviceName,serviceCode:item.serviceCode,
           saleId:body.data.saleId,payerOther,
           preselectedDiscount:pendingDiscount??null,
           linkedAppointments:body.data.linkedAppointments});
      }
      return body.data.status;
    }catch(e){setAttendanceMessage(e instanceof Error?e.message:'Checkout unavailable.');}
    finally{setCheckoutBusy(false);}
  };
  const cancelAppointment=async(item:Appointment,useCredit=false)=>{
    if(!options.data.writesEnabled||checkoutBusy)return;
    if(!window.confirm(useCredit
      ?'Cancel this appointment and use one service credit?'
      :'Cancel this appointment without a charge?'))return;
    setCheckoutBusy(true);setAttendanceMessage('');
    try{
      const response=await fetch(useCredit
        ?'/api/v1/staff/bookings/cancel-with-credit'
        :'/api/v1/staff/bookings/cancel',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({location:location.code,appointmentId:item.appointmentId}),
      });
      const body=await response.json() as {ok:boolean;error?:{code?:string;message?:string}};
      if(!response.ok||!body.ok)
        throw Error(body.error?.message??body.error?.code??'Unable to cancel appointment.');
      setAttendanceMessage(useCredit?'Cancelled and used one service credit.'
        :'Appointment cancelled without charge.');
      setRefresh(n=>n+1);
    }catch(e){setAttendanceMessage(e instanceof Error?e.message:'Cancellation unavailable.');}
    finally{setCheckoutBusy(false);}
  };

  const updateAttendance=async(
    appointment:Appointment,
    action:'CHECK_IN'|'COMPLETE'|'MARK_NO_SHOW',
  )=>{
    if(attendanceBusy||options.state!=='ready'||!options.data.writesEnabled)return;
    if(action==='MARK_NO_SHOW'&&!window.confirm(
      'Mark this appointment as a no-show? This will not adjust fees or credits.',
    ))return;
    setAttendanceBusy(appointment.appointmentId);
    setAttendanceMessage('');
    try{
      const response=await fetch('/api/v1/staff/bookings/attendance',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:location.code,appointmentId:appointment.appointmentId,action,
        }),
      });
      const result=await response.json() as {
        ok:boolean;data?:{status:string};error?:{message:string}
      };
      if(!response.ok||!result.ok)
        throw new Error(result.error?.message??'Could not update attendance.');
      setAttendanceMessage(
        action==='CHECK_IN'?'Guest checked in.':
        action==='COMPLETE'?'Appointment marked complete.':'Appointment marked no-show.',
      );
      setRefresh(value=>value+1);
    }catch(error){
      setAttendanceMessage(error instanceof Error?error.message:'Could not update attendance.');
    }finally{
      setAttendanceBusy('');
    }
  };

  const chosenService=options.data?.services?.find(service=>service.serviceId===serviceId);
  const book=async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();
    // The room's selected start must have an authoritative eligibility result.
    // Never attempt holds for a service shown before that check returns.
    if(slotPopup&&(slotAllowed===null||!slotAllowed.includes(serviceId)))return;
    if(bookingInProgress.current||!selectedGuest||!chosenService||
      !resourceId||!options.data.writesEnabled)return;
    bookingInProgress.current=true;
    setBookingBusy(true);
    setBookingStatus('');
    let activeHold=pendingHold;
    try{
      if(!activeHold){
        const startsAt=localBookingTimeToUTC(localStart,location.timezone);
        const endsAt=new Date(Date.parse(startsAt)+
          (chosenService.durationMinutes+chosenService.cleanupMinutes)*60000).toISOString();
        const response=await fetch('/api/v1/staff/bookings/holds',{
          method:'POST',credentials:'same-origin',
          headers:{'content-type':'application/json'},
          body:JSON.stringify({
            location:location.code,resourceId,serviceId,startsAt,endsAt,
            ttlSeconds:600,idempotencyKey:crypto.randomUUID()+'-staff-booking',
          }),
        });
        const result=await response.json() as {ok:boolean;data?:{holdId:string};error?:{message:string}};
        if(!response.ok||!result.ok||!result.data?.holdId)
          throw new Error(result.error?.message??'Room is not available.');
        activeHold={holdId:result.data.holdId,customerId:selectedGuest.customerId};
        setPendingHold(activeHold);
      }
      const response=await fetch('/api/v1/staff/bookings/confirm',{
        method:'POST',credentials:'same-origin',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:location.code,holdId:activeHold.holdId,
          customerId:activeHold.customerId,
        }),
      });
      const result=await response.json() as {ok:boolean;data?:{appointmentId:string};error?:{message:string}};
      if(!response.ok||!result.ok||!result.data?.appointmentId)
        throw new Error(result.error?.message??'Could not confirm the appointment.');
      setPendingHold(null);
      setSelectedGuest(null);
      setLocalStart('');
      setBookingStatus('Appointment confirmed. The calendar has been refreshed.');
      setSlotPopup(null);
      setRefresh(value=>value+1);
    }catch(error){
      // A failed confirmation must never strand the guest in a half-booked
      // state, require a second visible Confirm button, or block future slots.
      if(activeHold){
        try{
          const released=await fetch('/api/v1/staff/bookings/holds/release',{
            method:'POST',credentials:'same-origin',
            headers:{'content-type':'application/json'},
            body:JSON.stringify({location:location.code,holdId:activeHold.holdId}),
          });
          if(released.ok)setPendingHold(null);
        }catch{/* keep the hold state if release is unreachable */}
      }
      const message=error instanceof Error?error.message:'Unable to complete appointment.';
      setBookingStatus(/hours|ROOM_NOT_AVAILABLE|LOCATION_OUTSIDE/i.test(message)
        ?'This service cannot be booked at that time. Choose an earlier start.'
        :/access denied|sign-in required/i.test(message)
        ?message
        :/temporarily unavailable|service is not ready/i.test(message)
        ?'Booking temporarily unavailable. Please try again.'
        :/no longer available|time is no longer available|occupied/i.test(message)
        ?'That appointment time is unavailable. Choose another time.'
        :'The appointment could not be booked. '+message);
    }finally{
      bookingInProgress.current=false;
      setBookingBusy(false);
    }
  };

  const selectTeamMemberForSlot=async(member:{
    staffId:string;displayName:string;email?:string|null;phone?:string|null;
    customerId?:string|null;
  })=>{
    if(bookingBusy)return;
    if(member.customerId){
      setSelectedGuest({customerId:member.customerId,
        displayName:member.displayName,homeLocationCode:location.code,
        actorId:member.staffId});
      setSlotTeamMatches([]);setAutoBookCreated(true);return;
    }
    setBookingBusy(true);setBookingStatus('');
    try{
      const response=await fetch('/api/v1/staff/customers',{
        method:'POST',credentials:'same-origin',cache:'no-store',
        headers:{'content-type':'application/json'},
        body:JSON.stringify({
          location:location.code,displayName:member.displayName,
          staffActorId:member.staffId,smsOptIn:false,
          idempotencyKey:crypto.randomUUID()+'-booking-staff',
        }),
      });
      const body=await response.json() as {ok:boolean;
        data?:{customerId:string;displayName:string};error?:{message:string}};
      if(!response.ok||!body.ok||!body.data?.customerId)
        throw Error(response.status===409?
          'A matching customer profile requires review before booking.':
          body.error?.message??'Unable to select this customer.');
      setSelectedGuest({customerId:body.data.customerId,
        displayName:body.data.displayName,homeLocationCode:location.code,
        actorId:member.staffId});
      setSlotTeamMatches([]);setAutoBookCreated(true);
    }catch(e){setBookingStatus(e instanceof Error?e.message:'Customer selection failed.');}
    finally{setBookingBusy(false);}
  };
  const openConfiguredSlot=(roomId:string,minute:number,rect:DOMRect)=>{
    // A hold/confirmation belongs to the previously selected exact slot.
    // Do not let a second click replace its context mid-transaction.
    if(bookingInProgress.current||bookingBusy)return;
    if(rescheduleTarget){
      const target=rescheduleTarget;
      const local=day+'T'+String(Math.floor(minute/60)).padStart(2,'0')+':'+
        String(minute%60).padStart(2,'0');
      setAttendanceMessage('Rescheduling appointment…');
      void (async()=>{
        try{
          const response=await fetch('/api/v1/staff/bookings/reschedule',{
            method:'POST',credentials:'same-origin',
            headers:{'content-type':'application/json'},
            body:JSON.stringify({
              location:location.code,appointmentId:target.appointmentId,
              expectedResourceId:target.resourceId,
              expectedStartsAt:target.startsAt,
              newResourceId:roomId,newStartsAt:localBookingTimeToUTC(local,location.timezone),
              idempotencyKey:crypto.randomUUID(),
            }),
          });
          const body=await response.json() as {ok:boolean;
            error?:{message?:string;code?:string}};
          if(!response.ok||!body.ok)
            throw Error(body.error?.message??body.error?.code??'Reschedule failed.');
          setAttendanceMessage('Appointment rescheduled.');
          setRescheduleTarget(null);setRefresh(n=>n+1);
        }catch(e){setAttendanceMessage(
          e instanceof Error?e.message:'Reschedule failed.');}
      })();
      return;
    }
    const opts=(options.data?.services??[]).filter(service=>
      service.rooms.some(room=>room.resourceId===roomId));
    setResourceId(roomId);setServiceId(opts[0]?.serviceId??'');
    setLocalStart(day+'T'+String(Math.floor(minute/60)).padStart(2,'0')+':'+
      String(minute%60).padStart(2,'0'));
    setSelectedGuest(null);setPendingHold(null);setBookingStatus('');
    setSlotQuery('');setSlotMatches([]);setSlotTeamMatches([]);
    setSlotAllowed(null);setSlotEligibilityMessage('');setSlotEligibilityState('idle');
    setSlotStaffToAdd(null);setSlotSearchStatus('');
    setCreatingFromSlot(false);
    setSlotPopup({resourceId:roomId,startMinute:minute,
      left:Math.max(10,Math.min(rect.right+6,window.innerWidth-358)),
      top:Math.max(70,Math.min(rect.top-10,window.innerHeight-420))});
  };
  useEffect(()=>{
    if(!slotPopup||!localStart||options.state!=='ready')return;
    const controller=new AbortController();
    setSlotAllowed(null);setSlotEligibilityMessage('');setSlotEligibilityState('checking');
    let startsAt:string;
    try{
      startsAt=localBookingTimeToUTC(localStart,location.timezone);
    }catch{
      // An invalid/nonexistent or repeated DST wall-clock start cannot
      // authorize a booking, nor crash the signed-in Calendar.
      setSlotEligibilityState('error');
      setSlotEligibilityMessage('This appointment start time cannot be booked.');
      return;
    }
    const query=new URLSearchParams({
      location:location.code,resourceId:slotPopup.resourceId,startsAt,
    });
    void fetch('/api/v1/staff/booking-slot-services?'+query,{
      credentials:'same-origin',cache:'no-store',signal:controller.signal,
    }).then(async response=>{
      if(!response.ok)throw Error('Service eligibility unavailable.');
      const body=await response.json() as {ok:boolean;data?:{serviceIds:string[]}};
      if(!body.ok||!Array.isArray(body.data?.serviceIds))throw Error('Service eligibility unavailable.');
      if(controller.signal.aborted)return;
      setSlotAllowed(body.data.serviceIds);setSlotEligibilityState('ready');
      setServiceId(current=>body.data!.serviceIds.includes(current)?current:
        body.data!.serviceIds[0]??'');
      if(!body.data.serviceIds.length)
        setSlotEligibilityMessage('No services can be booked at this start time.');
    }).catch(()=>{if(!controller.signal.aborted){
      setSlotEligibilityState('error');
      setSlotEligibilityMessage('Service availability could not be verified.');
    }});
    return ()=>controller.abort();
  },[slotPopup?.resourceId,slotPopup?.startMinute,localStart,
    location.code,location.timezone,options.state]);
  useEffect(()=>{
    const query=slotQuery.trim();
    if(!slotPopup||query.length<2){
      setSlotTeamMatches([]);setSlotMatches([]);
      setSlotSearchStatus('');setSlotSearchBusy(false);return;
    }
    const controller=new AbortController();
    setSlotSearchBusy(true);setSlotSearchStatus('');
    const timer=setTimeout(()=>{
      const params=new URLSearchParams({location:location.code,q:query});
      // Match the header's TWO authorized datasets; employee profiles are not
      // automatically customer accounts and must never be booked by staffId.
      void Promise.all([
        fetch('/api/v1/staff/customers?'+params,{
          credentials:'same-origin',cache:'no-store',signal:controller.signal,
        }),
        fetch('/api/v1/staff/team/search?'+params,{
          credentials:'same-origin',cache:'no-store',signal:controller.signal,
        }),
      ]).then(async([customerResponse,teamResponse])=>{
        if(!customerResponse.ok||!teamResponse.ok)
          throw Error('Search unavailable.');
        const customerBody=await customerResponse.json() as {
          ok:boolean;data?:{customers:Customer[]}
        };
        const teamBody=await teamResponse.json() as {ok:boolean;data?:{
          teamMembers:Array<{staffId:string;displayName:string;
            email?:string|null;phone?:string|null;customerId?:string|null}>
        }};
        if(!customerBody.ok||!Array.isArray(customerBody.data?.customers)||
          !teamBody.ok||!Array.isArray(teamBody.data?.teamMembers))
          throw Error('Search unavailable.');
        if(controller.signal.aborted)return;
        const teamByActor=new Map(teamBody.data.teamMembers.map(member=>[member.staffId,member]));
        setSlotMatches(customerBody.data.customers.map(guest=>{
          const member=guest.actorId?teamByActor.get(guest.actorId):undefined;
          return member?{...guest,email:guest.email||member.email,
            phone:guest.phone||member.phone}:guest;
        }));
        setSlotTeamMatches(teamBody.data.teamMembers);
        setSlotSearchStatus(customerBody.data.customers.length||
          teamBody.data.teamMembers.length?'':'No matching customer profile found.');
      }).catch(()=>{if(!controller.signal.aborted){
        setSlotSearchStatus('Search unavailable.');
        setSlotMatches([]);setSlotTeamMatches([]);
      }}).finally(()=>{if(!controller.signal.aborted)setSlotSearchBusy(false);});
    },250);
    return ()=>{controller.abort();clearTimeout(timer);};
  },[slotQuery,slotPopup?.resourceId,slotPopup?.startMinute,location.code]);
  // Clicking 'Add And Book' performs the selected slot booking immediately
  // after the explicit customer-create API succeeds. No silent identity merge.
  useEffect(()=>{
    if(!autoBookCreated||!selectedGuest||!slotPopup)return;
    if(slotAllowed===null)return; // Wait for the actual room+start+duration rules.
    if(!slotAllowed.includes(serviceId)){
      setAutoBookCreated(false);
      setBookingStatus('This service cannot be booked at that time. Choose another service or start time.');
      return;
    }
    setAutoBookCreated(false);
    void book({preventDefault:()=>{}} as FormEvent<HTMLFormElement>);
  },[autoBookCreated,selectedGuest,slotPopup,slotAllowed,serviceId]);

  const popupRoom=calendarResources.data?.resources?.find(room=>room.resourceId===slotPopup?.resourceId);
  const popupServices=(options.data?.services??[]).filter(service=>
    service.rooms.some(room=>room.resourceId===slotPopup?.resourceId)&&
    slotAllowed!==null&&slotAllowed.includes(service.serviceId));

  return <div className={'staff-workspace'+(profileFocused?' is-profile-focused':'')}
    aria-label="Team Member scheduling">
    {!profileFocused&&<StaffCalendar day={day} timeZone={location.timezone} locationCode={location.code}
      appointments={calendar.state==='ready'?appointments:[]}
      blocks={blocks.state==='ready'?maintenance:[]}
       guestHolds={guestHolds.state==='ready'?guestHolds.data.holds:[]}
       onReleaseGuestHold={holdId=>void releaseGuestHold(holdId)}
       blockWriteEnabled={blockable.state==='ready'&&blockable.data.writesEnabled}
       onReleaseBlock={blockId=>void releaseBlock(blockId)}
       onReleaseBlocks={ids=>void releaseBlocks(ids)}
       onSelectBlockRange={openBlockRange}
      configuredStarts={configuredStarts.state==='ready'?configuredStarts.data.starts:[]}
      filterServices={options.state==='ready'?options.data.services.map(service=>({
        serviceId:service.serviceId,name:service.name,
        resourceIds:service.rooms.map(room=>room.resourceId),
      })):[]}
      configuredStartState={configuredStarts.state}
      bookingDataReady={calendar.state==='ready'&&blocks.state==='ready'&&
        guestHolds.state==='ready'&&configuredStarts.state==='ready'&&
        options.state==='ready'&&options.data.writesEnabled}
      resources={[
        ...(calendarResources.state==='ready'?calendarResources.data.resources:[])
          .map(item=>({resourceId:item.resourceId,name:item.name})),
        ...(options.state==='ready'
          ?options.data.services.flatMap(service=>service.rooms.map(room=>({
            resourceId:room.resourceId,name:room.name,
          }))):[]),
      ]}
      writesEnabled={options.state==='ready'&&options.data.writesEnabled}
      checkInEnabled={calendar.data?.checkInEnabled===true}
      checkoutBusy={checkoutBusy}
      onCheckout={(item,forceNewSale,discount)=>
        checkoutAppointment(item,forceNewSale,false,discount)}
      onCancelWithoutCharge={item=>void cancelAppointment(item)}
      onCancelWithCredit={item=>void cancelAppointment(item,true)}
      onCheckoutOtherCustomer={(item,discount)=>
        void checkoutAppointment(item,true,true,discount)}
      onReschedule={item=>{setRescheduleTarget(item);
        setAttendanceMessage('Select the new date, room and start time on the calendar.');}}

      resourceState={calendarResources.state} onGoPublic={onGoPublic}
      onOpenCancellations={onOpenCancellations}
      onOpenWaitlist={()=>setWaitlistOpen(true)}
      onOpenRoom={onOpenRoom}
      onDayChange={nextDay=>{
        if(nextDay!==day){setSlotPopup(null);setSlotAllowed(null);
          setAutoBookCreated(false);setBookingStatus('');setRescheduleTarget(null);}
        setDay(nextDay);
      }} onRefresh={()=>setRefresh(v=>v+1)}
      onOpenSlot={openConfiguredSlot}
      onOpenCustomer={item=>{
        // Calendar, Search and Sales share the ONE person profile route.
        if(onOpenCustomerProfile){
          onOpenCustomerProfile({
            customerId:item.customerId,
            displayName:item.customerName,homeLocationCode:null,
          });
          return;
        }
        setProfileFocused(true);setProfileTab('Appointments');
        void loadProfile({
          customerId:item.customerId,
          displayName:item.customerName,
          homeLocationCode:null,
        });
      }}
      onCheckIn={item=>void updateAttendance(item,'CHECK_IN')}
      onComplete={item=>void updateAttendance(item,'COMPLETE')}
    /> }
    {!profileFocused&&waitlistOpen&&<StaffWaitlistPanel
      locationCode={location.code} day={day}
      rooms={calendarResources.state==='ready'?calendarResources.data.resources.map(r=>({
        id:r.resourceId,name:r.name})):[]}
      services={options.state==='ready'?options.data.services.map(s=>({
        id:s.serviceId,name:s.name})):[]}
      onClose={()=>setWaitlistOpen(false)}/>}
    {!profileFocused&&blockRange&&<div className="ecfs-block-composer"
       onClick={()=>{if(!blockBusy)setBlockRange(null);}}>
       <form role="dialog" aria-label="Block out room times"
         onClick={event=>event.stopPropagation()} onSubmit={event=>void saveBlockRange(event)}>
         <h3>Block out</h3>
         <p>{blockRange.resourceIds.map(id=>blockable.data.resources.find(room=>
           room.resourceId===id)?.name??'Room').join(', ')}</p>
         <label>Start time
           <input type="time" value={timeFromMinute(blockRange.startMinute)}
             onChange={event=>setBlockRange(current=>current?{
               ...current,startMinute:minuteFromTime(event.target.value)}:current)}/>
         </label>
         <label>End time (extend to any later time)
           <input type="time" value={timeFromMinute(blockRange.endMinute%1440)}
             onChange={event=>setBlockRange(current=>current?{
               ...current,endMinute:minuteFromTime(event.target.value)}:current)}/>
         </label>
         <label>Comment / reason
           <input value={blockReason} required minLength={3} maxLength={300}
             onChange={event=>setBlockReason(event.target.value)}/>
         </label>
         <div className="ecfs-block-composer-actions">
           <button type="button" disabled={blockBusy}
             onClick={()=>setBlockRange(null)}>Cancel</button>
           <button type="submit" disabled={blockBusy}>
             {blockBusy?'Blocking…':'Block Out'}</button>
         </div>
       </form>
     </div>}
     {!profileFocused&&guestHolds.state==='error'&&<p role="alert">
      In-progress booking holds could not be loaded. Refresh before making a reservation.
    </p>}
    {!profileFocused&&attendanceMessage&&<p role="status" className="ecfs-attendance-status">{attendanceMessage}</p>}
    {!profileFocused&&!slotPopup&&bookingStatus&&<p role="status"
      className="ecfs-booking-status">{bookingStatus}</p>}
    {!profileFocused&&rescheduleTarget&&<div className="ecfs-reschedule-prompt" role="status">
      Rescheduling {rescheduleTarget.customerName}: select a room and new start time.
      <button type="button" onClick={()=>setRescheduleTarget(null)}>Cancel reschedule</button>
    </div>}
    {!profileFocused&&slotPopup&&<div className="ecfs-slot-popover" role="dialog"
      aria-label={'Book '+(popupRoom?.name??'room')}
      style={{left:slotPopup.left,top:slotPopup.top}}>
      <div className="ecfs-slot-popover-title">
        <strong>{popupRoom?.name??'Room'}</strong>
        <button type="button" aria-label="Close booking slot"
          onClick={()=>setSlotPopup(null)}>×</button>
      </div>
      {blockable.data.writesEnabled&&<button type="button"
         onClick={()=>openBlockRange([slotPopup.resourceId],slotPopup.startMinute,
           slotPopup.startMinute+15)}>Block Out</button>}
       <p>{String(Math.floor(slotPopup.startMinute/60)%12||12)+':'+
        String(slotPopup.startMinute%60).padStart(2,'0')+
        (slotPopup.startMinute<720?'am':'pm')}</p>
      {configuredStarts.data?.starts?.some(s=>
        s.resourceId===slotPopup.resourceId&&s.startMinute===slotPopup.startMinute&&
        s.source==='PROTOTYPE_V114_UNVERIFIED')&&
        <p className="ecfs-slot-historical">Historical prototype time, not verified.</p>}
      {slotEligibilityState==='ready'&&slotEligibilityMessage&&
        <p role="status">{slotEligibilityMessage}</p>}
      {slotEligibilityState==='error'
        ?<p role="alert">{slotEligibilityMessage}</p>
        :slotAllowed===null
        ?<p role="status">Checking service availability…</p>
        :!popupServices.length
        ?<p role="status">No services available for this room at this time.</p>
        :<form onSubmit={event=>void book(event)}>
          <fieldset className="ecfs-slot-service-list"><legend>Services</legend>
            {popupServices.map(service=><label key={service.serviceId}>
              <input type="radio" name="slot-service" value={service.serviceId}
                checked={serviceId===service.serviceId}
                onChange={()=>setServiceId(service.serviceId)}/>
              {service.name}{service.priceCents==null?'': ' ('+
                new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'})
                  .format(service.priceCents/100)+')'}
            </label>)}
          </fieldset>
          {!selectedGuest&&!creatingFromSlot&&<>
            <label htmlFor="ecfs-slot-guest-query">Customer</label>
            <div className="ecfs-slot-search">
              <input id="ecfs-slot-guest-query" aria-label="Find customer in booking slot"
                placeholder="Find Customer by Name, Email, Phone"
                value={slotQuery} onChange={event=>{setSlotQuery(event.target.value);
                  setSlotMatches([]);setSlotSearchStatus('');}}/>

            </div>
            {slotSearchBusy&&<p role="status">Searching…</p>}
            {slotSearchStatus&&<p role="status">{slotSearchStatus}</p>}
            {(slotMatches.length>0||slotTeamMatches.length>0)&&<div className="ecfs-slot-matches">
              {slotMatches.map(guest=><SearchPersonRow key={guest.customerId}
                person={{kind:'customer',id:guest.customerId,
                  displayName:guest.displayName,actorId:guest.actorId,
                  email:guest.email,phone:guest.phone}}
                locationCode={location.code}
                onSelect={()=>{setSelectedGuest(guest);setSlotMatches([]);
                  setSlotTeamMatches([]);setAutoBookCreated(true);}}
                ariaLabel={'Select customer '+guest.displayName}/>)}
              {slotTeamMatches.filter(member=>!slotMatches.some(guest=>
                guest.actorId===member.staffId)).map(member=>
                <SearchPersonRow key={'team-'+member.staffId}
                  person={{kind:'team',id:member.staffId,displayName:member.displayName,
                    email:member.email,phone:member.phone}}
                  locationCode={location.code}
                  onSelect={()=>void selectTeamMemberForSlot(member)}
                  ariaLabel={'Select customer '+member.displayName}/>)}
            </div>}
            <button type="button" className="ecfs-slot-add-customer"
              onClick={()=>setCreatingFromSlot(true)}>
              Add customer</button>
          </>}
          {selectedGuest&&!creatingFromSlot&&<p>Customer: <strong>{selectedGuest.displayName}</strong>
            <button type="button" onClick={()=>setSelectedGuest(null)}>Change</button></p>}
          {selectedGuest&&!creatingFromSlot&&bookingBusy&&
            <p role="status">Booking appointment…</p>}
          {bookingStatus&&<p role="status">{bookingStatus}</p>}
        </form>}
      {creatingFromSlot&&popupServices.length>0&&<NewCustomerDialog
        mode="booking" locationCode={location.code}
        initial={slotStaffToAdd?{
          firstName:slotStaffToAdd.displayName.split(/\s+/)[0],
          lastName:slotStaffToAdd.displayName.split(/\s+/).slice(1).join(' '),
          email:slotStaffToAdd.email??'',phone:slotStaffToAdd.phone??'',
          staffActorId:slotStaffToAdd.staffId,
        }:undefined}
        onClose={()=>{setCreatingFromSlot(false);setSlotStaffToAdd(null);}}
        onCreated={customer=>{
          setSelectedGuest(customer);setAutoBookCreated(true);
          setCreatingFromSlot(false);setSlotStaffToAdd(null);
          setSlotSearchStatus('');setSlotMatches([]);setSlotTeamMatches([]);
          setSlotQuery('');
        }}/>}
    </div>}
    {profileFocused&&profileTarget&&<section className="ecfs-customer-profile-page ecfs-record-profile" aria-label="Profile page">
      {profileFocused&&<aside className="ecfs-customer-profile-rail ecfs-record-sidebar"
        aria-label="Customer summary">
        {profileData?.state==='ready'&&profileData.data.profile.actorId
          ?<ProfileAvatar actorId={profileData.data.profile.actorId}
            displayName={profileData.data.profile.displayName}
            lastLoginAt={profileData.data.profile.lastLoginAt}/>
          :<div className="ecfs-record-avatar" aria-label="No profile photo"/>}
        <div className="ecfs-profile-sidebar-field ecfs-record-side-box">
          <strong>Default Location</strong>
          <span>{profileTarget.homeLocationCode??'Not assigned'}</span>
        </div>
        {profileData?.state==='ready'&&<>
          <div className="ecfs-profile-sidebar-field">
            <strong>Email</strong>
            <span>{profileData.data.profile.email??'No email recorded'}</span>
          </div>
          <div className="ecfs-profile-sidebar-field">
            <strong>Phone</strong>
            <span>{profileData.data.profile.phoneE164??'No phone recorded'}</span>
          </div>
        </>}
        <button type="button" className="ecfs-helm-begin-sale"
          onClick={()=>onBeginCustomerSale({
            customerId:profileTarget.customerId,displayName:profileTarget.displayName,
          })}>
          Begin Sale
        </button>
        <button type="button" onClick={()=>{
          setWaiverOpen(true);void loadWaivers(profileTarget);
        }}>Waivers</button>
        {profileData?.state==='ready'&&
        <form className="booking-form"
        onSubmit={event=>void saveProfile(event)}>
        <p className="muted">Version {profileData.data.profile.version}. Changes are checked against the latest saved version.</p>
        <label className="field">Customer full name
          <input aria-label="Customer profile name" value={profileName}
            onChange={event=>{setProfileName(event.target.value);profileKey.current=null;
              setProfileDuplicateMatches(null);}} minLength={2} maxLength={120} required/>
        </label>
        <label className="field">Contact email
          <input aria-label="Customer profile email" type="email" value={profileEmail}
            onChange={event=>{setProfileEmail(event.target.value);profileKey.current=null;}}
            maxLength={320}/>
        </label>
        <label className="field">Contact phone
          <input aria-label="Customer profile phone" type="tel" value={profilePhone}
            placeholder="10-digit U.S. number or +international" maxLength={35}
            onChange={event=>{setProfilePhone(event.target.value);profileKey.current=null;}}/>
        </label>
        <fieldset className="ecfs-customer-birthday">
          <legend>Birthday</legend>
          <select aria-label="Profile birthday month" value={profileBirthdayMonth}
            onChange={event=>{setProfileBirthdayMonth(event.target.value);profileKey.current=null;}}>
            <option value="">month</option>
            {['January','February','March','April','May','June','July','August',
              'September','October','November','December'].map((month,index)=>
              <option key={month} value={index+1}>{month}</option>)}
          </select>
          <input type="number" aria-label="Profile birthday day" min={1} max={31}
            placeholder="day" value={profileBirthdayDay}
            onChange={event=>{setProfileBirthdayDay(event.target.value);profileKey.current=null;}}/>
          <input type="number" aria-label="Profile birthday year" min={1900} max={2100}
            placeholder="year (optional)" value={profileBirthdayYear}
            onChange={event=>{setProfileBirthdayYear(event.target.value);profileKey.current=null;}}/>
        </fieldset>
        <label className="field">Address
          <input aria-label="Customer profile address" value={profileRegistration.address??''}
            maxLength={500} onChange={event=>editRegistration('address',event.target.value)}/>
        </label>
        <label className="field">Postal Code
          <input aria-label="Customer profile postal code" value={profileRegistration.postalCode??''}
            maxLength={30} onChange={event=>editRegistration('postalCode',event.target.value)}/>
        </label>
        <fieldset className="ecfs-customer-emergency">
          <legend>Emergency Contact Info</legend>
          <label className="field">Emergency Contact Name
            <input aria-label="Customer profile emergency contact name"
              value={profileRegistration.emergencyContactName??''} maxLength={120}
              onChange={event=>editRegistration('emergencyContactName',event.target.value)}/>
          </label>
          <label className="field">Emergency Contact Phone
            <input aria-label="Customer profile emergency contact phone" type="tel"
              value={profileRegistration.emergencyContactPhone??''} maxLength={30}
              onChange={event=>editRegistration('emergencyContactPhone',event.target.value)}/>
          </label>
        </fieldset>
        <label className="field">How did you hear about us?
          <select aria-label="Customer profile referral source"
            value={profileRegistration.heardFrom??''}
            onChange={event=>editRegistration('heardFrom',event.target.value)}>
            <option value="">Select</option>
            {profileRegistration.heardFrom&&
              !profileReferralOptions.includes(profileRegistration.heardFrom)&&
              <option value={profileRegistration.heardFrom}>
                {profileRegistration.heardFrom}</option>}
            {profileReferralOptions.map(option=>
              <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <fieldset className="ecfs-profile-email-preferences">
          <legend>Email Preferences</legend>
          {!profileData.data.profile.emailPreferences&&
            <p className="muted">No saved preferences on this customer profile.</p>}
          {preferenceFields.map(([key,label])=><label key={key}>
            <input type="checkbox" checked={profileEmailPreferences?.[key]??false}
              onChange={event=>{
                setProfileEmailPreferences(current=>({
                  appointmentConfirmation:false,appointmentReminder:false,
                  saleReceipt:false,nonEssential:false,mailingList:false,
                  ...current,[key]:event.target.checked,
                }));
                profileKey.current=null;
              }}/>
            {label}
          </label>)}
          <p className="muted">Checkbox changes save automatically. They do not override consent or the global opt-out list.</p>
        </fieldset>
        <p className="muted">Contact preferences are recorded statements only. They do not bypass the company-wide opt-out list.</p>
        <label className="field">Email preference
          <select aria-label="Customer email preference" value={profileEmailConsent}
            onChange={event=>{setProfileEmailConsent(event.target.value as ProfileConsent);
              setProfileEmailEvidence('');profileKey.current=null;}}>
            <option value="UNKNOWN">Not recorded</option>
            <option value="OPTED_IN">Explicit opt-in reported</option>
            <option value="OPTED_OUT">Opted out</option>
          </select>
        </label>
        {profileEmailConsent!==profileData.data.profile.emailConsent&&profileEmailConsent!=='UNKNOWN'&&
          <label className="field">Email preference evidence
            <select aria-label="Email preference evidence" required value={profileEmailEvidence}
              onChange={event=>setProfileEmailEvidence(event.target.value as ConsentEvidence)}>
              <option value="">Select source</option>
              <option value="IN_PERSON">Guest stated in person</option>
              <option value="WRITTEN_REQUEST">Written request</option>
              <option value="ONLINE_REQUEST">Guest online request</option>
              <option value="EXISTING_DOCUMENTATION">Existing verified documentation</option>
            </select>
          </label>}
        <label className="field">SMS preference
          <select aria-label="Customer SMS preference" value={profileSmsConsent}
            onChange={event=>{setProfileSmsConsent(event.target.value as ProfileConsent);
              setProfileSmsEvidence('');profileKey.current=null;}}>
            <option value="UNKNOWN">Not recorded</option>
            <option value="OPTED_IN">Explicit opt-in reported</option>
            <option value="OPTED_OUT">Opted out</option>
          </select>
        </label>
        {profileSmsConsent!==profileData.data.profile.smsConsent&&profileSmsConsent!=='UNKNOWN'&&
          <label className="field">SMS preference evidence
            <select aria-label="SMS preference evidence" required value={profileSmsEvidence}
              onChange={event=>setProfileSmsEvidence(event.target.value as ConsentEvidence)}>
              <option value="">Select source</option>
              <option value="IN_PERSON">Guest stated in person</option>
              <option value="WRITTEN_REQUEST">Written request</option>
              <option value="ONLINE_REQUEST">Guest online request</option>
              <option value="EXISTING_DOCUMENTATION">Existing verified documentation</option>
            </select>
          </label>}
        {/* Editing a customer is not contingent on the unrelated room-booking
            service readiness feed. Staff authorization belongs to the PATCH API. */}
        {(profileName.trim()!==profileData.data.profile.displayName||
          profileEmail.trim()!==(profileData.data.profile.email??'')||
          profilePhone.trim()!==(profileData.data.profile.phoneE164??'')||
          profileBirthdayMonth!==String(profileData.data.profile.birthdayMonth??'')||
          profileBirthdayDay!==String(profileData.data.profile.birthdayDay??'')||
          profileBirthdayYear!==String(profileData.data.profile.birthdayYear??'')||
          JSON.stringify(profileRegistration)!==JSON.stringify({
            ...emptyCustomerRegistration(),...profileData.data.profile.registrationDetails})||
          profileEmailConsent!==profileData.data.profile.emailConsent||
          profileSmsConsent!==profileData.data.profile.smsConsent||
          (profileEmailPreferences!==null&&
            JSON.stringify(profileEmailPreferences)!==
              JSON.stringify(profileData.data.profile.emailPreferences)))&&
          <p role="status">{profileBusy?'Saving preferences…':
            (profileEmailConsent!==profileData.data.profile.emailConsent&&
              profileEmailConsent!=='UNKNOWN'&&!profileEmailEvidence)||
            (profileSmsConsent!==profileData.data.profile.smsConsent&&
              profileSmsConsent!=='UNKNOWN'&&!profileSmsEvidence)
              ?'Choose the preference source to save it.':
               'Saving changes automatically…'}</p>}
        {profileDuplicateMatches&&profileDuplicateMatches.length>0&&
          <div><p>Existing customer profiles with that name:</p>
            <ul className="event-list">{profileDuplicateMatches.map(candidate=><li key={candidate.customerId}>
              <strong>{candidate.displayName}</strong>
              <small>Home: {candidate.homeLocationCode??'Unassigned'}</small>
              <button type="button" onClick={()=>void loadProfile(candidate)}>Review matching profile</button>
            </li>)}</ul>
            <button type="button" disabled={profileBusy} onClick={()=>void saveProfile(undefined,
              profileDuplicateMatches.map(x=>x.customerId))}>
              Confirm separate customer with same name
            </button>
          </div>}
        <button type="button" onClick={()=>void loadProfile(profileTarget)}>Reload saved profile</button>
      </form>}
      </aside>}
      <div className="ecfs-record-main">

      <div className="ecfs-record-heading"><div>
        <h1>{profileTarget.displayName}</h1>
      </div><button type="button" className="ecfs-record-back" onClick={()=>{
        profileRequest.current?.abort();historyRequest.current?.abort();creditRequest.current?.abort();
        purchaseRequest.current?.abort();setPurchaseHistory(null);
        waiverRequest.current?.abort();setWaiverOpen(false);setWaivers(null);
        availableCreditRequest.current?.abort();
        setProfileCredits(null);setAvailableCredits(null);setCreditAvailability('available');
        setProfileHistory(null);setProfileHistoryStatus('');setProfileHistoryBusy(false);
        setProfileTarget(null);setProfileData(null);setProfileFocused(false);
        setProfileMessage('');setProfileDuplicateMatches(null);profileKey.current=null;
      }} >Back to calendar</button></div>
      {profileData?.state==='ready'&&<section className="ecfs-profile-credit-summary ecfs-record-top-detail"
        aria-label="Available service credit summary">
        <span className="ecfs-record-label">Available Service Credits</span>
        {!availableCredits&&<p role="status">Loading service credits…</p>}
        {availableCredits&&<Message state={availableCredits.state}
          loginMessage="Sign in to view service credits."
          message="Available service credits cannot be verified."/>}
        {creditSummary&&(
          creditSummary.length>0
            ?<ul className="ecfs-record-credit-list">{creditSummary.map(item=><li key={item.code}>
                <span>{item.name}</span><strong>{item.quantity}</strong>
              </li>)}</ul>
            :<p role="status">No available service credits recorded.</p>
        )}
      </section>}

      {waiverOpen&&profileTarget&&<div className="ecfs-public-location-modal-backdrop"
        onClick={()=>{waiverRequest.current?.abort();setWaiverOpen(false);}}>
        <section role="dialog" aria-modal="true" aria-label="Customer Waiver History"
          className="ecfs-public-location-modal"
          onClick={event=>event.stopPropagation()}>
          <button type="button" aria-label="Close waiver history"
            onClick={()=>{waiverRequest.current?.abort();setWaiverOpen(false);}}>×</button>
          <h3>Waivers</h3>
          {waivers?.state==='loading'&&<p role="status">Loading signed waivers…</p>}
          {waivers?.state==='error'&&<p role="alert">Waiver history unavailable.</p>}
          {waivers?.state==='ready'&&<>
            <table className="ecfs-record-table">
              <thead><tr><th>Signed</th><th>Waiver</th><th>Revision</th></tr></thead>
              <tbody>{waivers.data.waivers.map(item=><tr key={item.acceptanceId}>
                <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',
                  timeStyle:'short',timeZone:location.timezone})
                  .format(new Date(item.signedAt))}</td>
                <td>{item.title}{item.inheritedFromMergedAccount&&
                  <small> (merged account)</small>}</td>
                <td>{item.revision}</td>
              </tr>)}</tbody>
            </table>
            {!waivers.data.waivers.length&&
              <p role="status">No signed waivers recorded for this customer.</p>}
            {waiverError&&<p role="alert">{waiverError}</p>}
            {waivers.data.hasMore&&waivers.data.nextCursor&&
              <button type="button" disabled={waiverBusy}
                onClick={()=>void loadWaivers(profileTarget,waivers.data.nextCursor!)}>
                {waiverBusy?'Loading…':'Load older waivers'}
              </button>}
          </>}
        </section>
      </div>}
      {profileFocused&&<nav className="ecfs-customer-tabs ecfs-record-tabs" aria-label="Profile tabs">
        <button type="button" aria-current={profileTab==='Appointments'?'page':undefined}
          onClick={()=>setProfileTab('Appointments')}>Appointments</button>
        <button type="button" aria-current={profileTab==='Service Credits'?'page':undefined}
          onClick={()=>{setCreditAvailability('available');setProfileTab('Service Credits');}}>
          Service Credits
        </button>
        {(['Memberships','Shares'] as const).map(item=><button type="button"
          key={item} disabled title={item+' requires verified account records'}>
          {item}
        </button>)}
        <button type="button" aria-current={profileTab==='Purchases'?'page':undefined}
          onClick={()=>setProfileTab('Purchases')}>Purchases</button>
        <button type="button" aria-current={profileTab==='Notes'?'page':undefined}
          onClick={()=>setProfileTab('Notes')}>Notes</button>
        {(['Shifts','Permissions'] as const).map(item=><button type="button"
          key={item} disabled title={item+' requires a separately authorized record'}>
          {item}
        </button>)}
      </nav>}
      <div className="ecfs-record-tab-content" aria-label="Customer profile content">
      {profileData&&<Message state={profileData.state}
        loginMessage="Sign in to view customer details."/>}
      {profileMessage&&<p role="status">{profileMessage}</p>}
      {profileFocused&&profileTab==='Purchases'&&
        <section className="profile-history" aria-label="Customer purchases">
          <h3>Purchases</h3>
          <p className="muted">Recorded PWA cash purchases and verified
            Stripe TEST payments. Prior FloatHelm purchase history is not included.</p>
          {purchaseHistory?.state==='loading'&&
            <p role="status">Loading purchases…</p>}
          {purchaseHistory?.state==='error'&&
            <p role="alert">Purchase records are unavailable.</p>}
          {purchaseHistory?.state==='ready'&&
            (purchaseHistory.data.purchases.length?
              <div className="ecfs-record-table-scroll">
                <table className="ecfs-record-table">
                  <thead><tr>
                    <th>Date</th><th>Purchase</th><th>Location</th><th>Amount</th>
                  </tr></thead>
                  <tbody>{purchaseHistory.data.purchases.map(item=><tr key={item.id}>
                    <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',
                      timeStyle:'short',timeZone:location.timezone})
                      .format(new Date(item.occurredAt))}</td>
                    <td>{item.description}{item.kind==='STRIPE_TEST'&&
                      <small> (Stripe TEST)</small>}</td>
                    <td>{item.locationName}</td>
                    <td>{new Intl.NumberFormat('en-US',{style:'currency',
                      currency:'USD'}).format(item.subtotalCents/100)}</td>
                  </tr>)}</tbody>
                </table>
                {purchaseHistory.data.hasMore&&purchaseHistory.data.nextCursor&&
                  <button type="button" disabled={purchaseBusy}
                    onClick={()=>void loadPurchases(profileTarget!,
                      purchaseHistory.data.nextCursor!)}>
                    {purchaseBusy?'Loading…':'Load older purchases'}
                  </button>}
              </div>:<p role="status">No recorded purchases for this customer.</p>)}
          {purchaseError&&<p role="alert">{purchaseError}</p>}
        </section>}
      {profileFocused&&profileTab==='Notes'&&
        <section className="profile-history ecfs-profile-notes" aria-label="Customer profile notes">
          <h3>Customer Notes</h3>
          <p>Notes are internal only. An important note marked with the red ! also appears on this customer's Schedule appointments.</p>
          <label>Add customer note
            <textarea aria-label="Add customer note" value={profileNoteText}
              rows={3} maxLength={5000} disabled={profileNoteBusy}
              onChange={event=>setProfileNoteText(event.target.value)}/>
          </label>
          <div className="ecfs-profile-note-actions">
            <button type="button" className={'ecfs-note-important '+(profileNoteImportant?'on':'')}
              aria-label="Mark new note important" aria-pressed={profileNoteImportant}
              disabled={profileNoteBusy}
              onClick={()=>setProfileNoteImportant(value=>!value)}>!</button>
            <button type="button" disabled={profileNoteBusy||!profileNoteText.trim()}
              onClick={()=>void addCustomerNote()}>{profileNoteBusy?'Saving…':'Post'}</button>
          </div>
          {profileNoteStatus&&<p role="alert">{profileNoteStatus}</p>}
          {profileNotes?.state==='loading'&&<p role="status">Loading customer notes…</p>}
          {profileNotes?.state==='error'&&<p role="alert">Customer notes are unavailable.</p>}
          {profileNotes?.state==='ready'&&(
            profileNotes.data.notes.length===0?<p>No customer notes.</p>:
              <ol className="ecfs-note-history">
                {profileNotes.data.notes.map(note=><li key={note.noteId}>
                  <div className="ecfs-note-header">
                    <strong>{note.authorName}</strong>
                    <time>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',timeStyle:'short',
                      timeZone:location.timezone}).format(new Date(note.createdAt))}</time>
                    <button type="button"
                      className={'ecfs-note-important '+(note.important?'on':'')}
                      title={note.important?'Unmark important':'Mark important'}
                      aria-label={'Toggle important for note by '+note.authorName}
                      aria-pressed={note.important} disabled={profileNoteBusy}
                      onClick={()=>void modifyCustomerNote(note.noteId,note.version,!note.important)}>!</button>
                    <button type="button" disabled={profileNoteBusy}
                      aria-label={'Delete note by '+note.authorName}
                      onClick={()=>{
                        if(window.confirm('Delete this customer note?'))
                          void modifyCustomerNote(note.noteId,note.version);
                      }}>Delete</button>
                  </div>
                  <p>{note.text}</p>
                </li>)}
              </ol>
          )}
        </section>}
      {profileFocused&&profileTab==='Service Credits'&&
        <section className="profile-history" aria-label="Customer service credits">
          <h3>Service Credits</h3>
          <div role="group" aria-label="Service credit availability">
            <button type="button" aria-pressed={creditAvailability==='available'}
              onClick={()=>setCreditAvailability('available')}>Available</button>
            <button type="button" aria-pressed={creditAvailability==='unavailable'}
              onClick={()=>setCreditAvailability('unavailable')}>Unavailable</button>
          </div>
          {visibleCredits&&<Message state={visibleCredits.state}
            loginMessage="Sign in to view service credits."
            message="Service credit records are temporarily unavailable."/>}
          {expiryStatus&&<p role="status">{expiryStatus}</p>}
          {visibleCredits?.state==='ready'&&(
            visibleCredits.data.credits.length===0
              ?<p role="status">No {creditAvailability} service credits in this ledger.</p>
              :<div className="ecfs-record-table-scroll">
                <table className="ecfs-record-table">
                  <thead><tr><th>Service</th><th>Remaining</th><th>Issued</th><th>Expiration</th></tr></thead>
                  <tbody>{visibleCredits.data.credits.map(credit=><tr key={credit.creditId}>
                    <td>{credit.serviceName}</td>
                    <td>{credit.remainingUses}</td>
                    <td>{new Intl.DateTimeFormat('en-US',{dateStyle:'medium',timeZone:'UTC'})
                      .format(new Date(credit.issuedAt))}</td>
                    <td>
                      <button type="button" aria-label={'Edit expiration for '+credit.serviceName}
                        disabled={expiryBusy}
                        onClick={()=>{
                          setEditCreditExpiry(credit.creditId);
                          setExpiresNever(credit.expiresAt===null);
                          setExpiresOn(credit.expiresAt?toCreditExpiryDay(credit.expiresAt):'');
                          setExpiryStatus('');
                        }}>
                        {credit.expiresAt?toCreditExpiryDay(credit.expiresAt):'No expiration'}
                      </button>
                      {editCreditExpiry===credit.creditId&&
                        <div className="ecfs-credit-expiration-editor">
                          <label><input type="checkbox" checked={expiresNever}
                            disabled={expiryBusy}
                            onChange={event=>setExpiresNever(event.target.checked)}/>
                            No expiration</label>
                          {!expiresNever&&<label>Expiration date
                            <input type="date" aria-label="Credit expiration date"
                              value={expiresOn} required disabled={expiryBusy}
                              onChange={event=>setExpiresOn(event.target.value)}/>
                          </label>}
                          <button type="button" disabled={expiryBusy||
                            (!expiresNever&&!expiresOn)}
                            onClick={()=>void saveCreditExpiration(
                              credit.creditId,credit.expiresAt)}>
                            {expiryBusy?'Saving…':'Save'}
                          </button>
                          <button type="button" disabled={expiryBusy}
                            onClick={()=>setEditCreditExpiry(null)}>Cancel</button>
                        </div>}
                    </td>
                  </tr>)}</tbody>
                </table>
              </div>)}
        </section>}
      {(!profileFocused||profileTab==='Appointments')&&
        <section className="profile-history" aria-label="Customer appointment history">
        <h3>Appointment history</h3>
        <label className="ecfs-profile-appointment-filter">
          Appointment status
          <select aria-label="Customer appointment status" value={appointmentHistoryFilter}
            onChange={event=>setAppointmentHistoryFilter(
              event.target.value as AppointmentHistoryFilter)}>
            {appointmentHistoryFilters.map(option=><option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        {profileHistory&&<Message state={profileHistory.state}
          loginMessage="Sign in to view appointment history."
          message="Customer appointment history is temporarily unavailable."/>}
        {profileHistoryStatus&&<p role="alert">{profileHistoryStatus}</p>}
        {profileHistory?.state==='ready'&&<>
          {profileHistory.data.appointments.filter(item=>
            matchesHistoryFilter(item,appointmentHistoryFilter)).length===0
            ?<p className="muted">{profileHistory.data.appointments.length===0
              ?'No appointments recorded for this guest.':
              'No appointments found for this status in loaded history.'}</p>
            :<div className="ecfs-record-table-scroll">
              <table className="ecfs-record-table">
                <thead><tr>
                  <th>Beginning</th><th>Service</th><th>Room</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {profileHistory.data.appointments.filter(item=>
                    matchesHistoryFilter(item,appointmentHistoryFilter)).map(item=>
                    <tr key={item.appointmentId}>
                      <td>{new Intl.DateTimeFormat('en-US',{
                        timeZone:item.locationTimezone,year:'numeric',
                        month:'short',day:'numeric',hour:'numeric',minute:'2-digit',
                      }).format(new Date(item.startsAt))}</td>
                      <td>{item.serviceName}</td>
                      <td>{item.locationName} · {item.resourceName}</td>
                      <td>{item.status.replaceAll('_',' ')}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
          {profileHistory.data.hasMore&&profileHistory.data.nextCursor&&
            <button type="button" disabled={profileHistoryBusy} onClick={()=>void loadProfileHistory(
              profileTarget,profileHistory.data.nextCursor!,
            )}>{profileHistoryBusy?'Loading earlier visits…':'Load older appointments'}</button>}
        </>}
      </section>}
      </div>
      </div>
    </section>}

  </div>;
}

function changePublicBookingDay(day:string,offset:number):string {
  const noon=Date.parse(day+'T12:00:00Z');
  return Number.isFinite(noon)
    ?new Date(noon+offset*86400000).toISOString().slice(0,10):day;
}

export function GuestWorkspace({location}:{location:SpaLocation}) {
  const [upcoming,setUpcoming]=useState<AsyncData<{appointments:OwnAppointment[]}>>({
    state:'loading',data:{appointments:[]},
  });
  useEffect(()=>{
    const controller=new AbortController();
    const start=Math.floor(Date.now()/60000)*60000;
    const query=new URLSearchParams({
      from:new Date(start).toISOString(),
      to:new Date(start+90*86400000).toISOString(),
    });
    setUpcoming({state:'loading',data:{appointments:[]}});
    void requestData<{appointments:OwnAppointment[]}>(
      '/api/v1/guest/appointments?'+query,controller.signal,
    ).then(result=>{if(!controller.signal.aborted)setUpcoming(result);})
      .catch(()=>{if(!controller.signal.aborted)setUpcoming({
        state:'error',data:{appointments:[]},
      });});
    return ()=>controller.abort();
  },[]);

  const [day,setDay]=useState(()=>localDay(new Date(),location.timezone));
  const [catalog,setCatalog]=useState<AsyncData<{services:Service[]}>>({
    state:'loading',data:{services:[]},
  });
  const [serviceCode,setServiceCode]=useState('');
  const [slots,setSlots]=useState<AsyncData<{slots:Slot[]}>|null>(null);
  useEffect(()=>{
    setDay(localDay(new Date(),location.timezone));
    setServiceCode('');
    setSlots(null);
    const controller=new AbortController();
    setCatalog({state:'loading',data:{services:[]}});
    void requestData<{services:Service[]}>(
      '/api/v1/services?'+new URLSearchParams({location:location.code}),
      controller.signal)
      .then(next=>{if(!controller.signal.aborted){
        setCatalog(next);
        setServiceCode(next.data?.services?.[0]?.code??'');
      }})
      .catch(()=>{if(!controller.signal.aborted)setCatalog({state:'error',data:{services:[]}});});
    return ()=>controller.abort();
  },[location.code,location.timezone]);
  useEffect(()=>{
    if(!serviceCode){setSlots(null);return;}
    const controller=new AbortController();
    setSlots({state:'loading',data:{slots:[]}});
    const query=new URLSearchParams({location:location.code,service:serviceCode,date:day});
    void requestData<{slots:Slot[]}>('/api/v1/slots?'+query,controller.signal)
      .then(next=>{if(!controller.signal.aborted)setSlots(next);})
      .catch(()=>{if(!controller.signal.aborted)setSlots({state:'error',data:{slots:[]}});});
    return ()=>controller.abort();
  },[location.code,serviceCode,day]);

  return <section className="panel work-panel" aria-label="Guest booking availability">
    <h2>Service availability</h2>
    <p className="muted">Configured booking times are shown only when rooms and operating hours permit them. Times are not held until a reservation is completed.</p>
    <Message state={catalog.state}/>
    {catalog.state==='ready'&&catalog.data.services.length===0&&
      <p role="status">Online services have not been configured for this location.</p>}
    {catalog.state==='ready'&&catalog.data.services.length>0&&<>
      <div className="ecfs-public-date-navigation" aria-label="Public booking date navigation">
        <button type="button" aria-label="Previous booking day"
          onClick={()=>setDay(current=>changePublicBookingDay(current,-1))}>←</button>
        <strong>{new Intl.DateTimeFormat('en-US',{weekday:'long',month:'long',day:'numeric',year:'numeric',timeZone:'UTC'})
          .format(new Date(day+'T12:00:00Z'))}</strong>
        <button type="button" aria-label="Next booking day"
          onClick={()=>setDay(current=>changePublicBookingDay(current,1))}>→</button>
      </div>
      <div className="inline-form">
        <label className="field">Service
          <select aria-label="Booking service" value={serviceCode}
            onChange={event=>setServiceCode(event.target.value)}>
            {catalog.data.services.map(service=><option key={service.code} value={service.code}>
              {service.name} ({service.durationMinutes} min)
            </option>)}
          </select>
        </label>
        <label className="field">Booking date
          <input aria-label="Booking date" type="date" value={day}
            onChange={event=>setDay(event.target.value)}/>
        </label>
      </div>
      {slots&&<Message state={slots.state}/>}
      {slots?.state==='ready'&&(slots.data.slots.length
        ? <ul className="event-list">{slots.data.slots.map(item=><li key={item.startsAt}>
          <strong>{readable(item.startsAt,location.timezone)}</strong>
          <span>{item.availableResourceCount} room{item.availableResourceCount===1?'':'s'} currently available</span>
          <small>Subject to final availability at booking</small>
        </li>)}</ul>
        : <p role="status">No bookable times for this service and date.</p>)}
    </>}
    <div className="guest-upcoming">
      <h2>Your upcoming visits</h2>
      <Message state={upcoming.state}
        loginMessage="Sign in to see your booked appointments."/>
      {upcoming.state==='ready'&&(upcoming.data.appointments.length
        ? <ul className="event-list">{upcoming.data.appointments.map(appointment=><li key={appointment.appointmentId}>
          <strong>{appointment.serviceName} · {appointment.locationName}</strong>
          <span>{new Intl.DateTimeFormat('en-US',{
            dateStyle:'medium',timeStyle:'short',
            timeZone:appointment.locationTimezone,
          }).format(new Date(appointment.startsAt))}</span>
          <small>Status: {appointment.status}</small>
        </li>)}</ul>
        : <p role="status">No upcoming appointments in the next 90 days.</p>)}
    </div>
  </section>;
}
